import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import {
    DocumentTemplateView,
    DocumentTemplateViewItem,
} from '@deltek/specification-client/core/Models/documentTemplate'
import { Nugget } from '@deltek/specification-client/core/Models/nugget'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ProjectManufacturerProductDetail } from '@deltek/specification-client/core/Models/projectManufacturerProductDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import {
    SetProjectContentItemIsHiddenInput,
    ViewItemReorderInput,
} from '@deltek/specification-client/core/Models/projectProductFamily'
import {
    AddProjectContentItemInput,
    RemoveProjectContentInput,
    UpdateProjectContent,
} from '@deltek/specification-client/core/Models/projectProductProperty'
import { ViewItemReorderPTPInput } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import { ProjectUniformatClassification } from '@deltek/specification-client/core/Models/projectUniformatClassification'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { UniformatClassification } from '@deltek/specification-client/core/Models/uniformatClassification'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import TableLoadingSkeleton from '@deltek/specification-client/core/Widgets/SpecTable/TableLoadingSkeleton'
import Backdrop from '@material-ui/core/Backdrop/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme, makeStyles } from '@material-ui/core/styles'
import DoubleArrowRoundedIcon from '@material-ui/icons/DoubleArrowRounded'
import {
    RemoveProductTypeContentItem,
    RemoveProjectContentItemMutation,
} from 'api/graphQL/mutations/projectContentItemRemove.mutation'
import {
    AddNewProductTypeContentItem,
    AddNewProductTypeInputMutation,
} from 'api/graphQL/mutations/projectElement.mutations'
import {
    AddNewContentItem,
    AddNewSustainabilityTopic,
    ReorderFamilyContent,
    ReorderProductTypeContent,
    SetProjectContentItemIsHidden,
} from 'api/graphQL/mutations/projectProductFamily.mutations'
import {
    GetAssebmlyElementChanges,
    GetFamilyElementChanges,
    GetProductTypeChanges,
} from 'api/graphQL/queries/changes.queries'
import {
    GetProductTypeProductListingsInfo,
    GetProjectNuggetForProductListingsQuery,
    GetProjectNuggetQuery,
    GetProjectProductFamilyElementMetadata,
    GetProjectProductFamilyQuery,
    GetProjectProductFamilyQueryWithTable,
    GetProjectProductTypeProductListingsInfo,
    GetProjectProductTypeProperties,
    GetUpdatedProjectProductFamilyProductListingQuery,
    NuggetProductTypePropertiesViewQuery,
    NuggetQuery,
    ProductFamilyDocumentTemplateViewQuery,
    ProductFamilyQuery,
    ProjectNuggetProductTypePropertiesViewQuery,
    ProjectProductFamilyDocumentTemplateViewQuery,
} from 'api/graphQL/queries/projectElements.queries'
import {
    setElementNotesCount,
    setElementTrackedChanges,
    setFamilyProjectElementMetadata,
    setProjectElementMetadata,
    updateElementNotesCount,
} from 'api/graphQL/reactiveVars/element'
import {
    resetOnFocusProperty,
    setArticleToFocus,
    workspaceProperty,
} from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isWorkspaceActionsNotDefault,
    newlyAddedParagraphText,
    setMoveUpAndDownTrigger,
    setPromoteDemoteWorkspaceTemplateData,
    setReactiveWorkspaceActions,
    setWorkspaceTemplateData,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    ProductFamilyWithTable,
    contentTeamProjectId,
    handleDTVReorder,
    isContentTeam,
    isOmWorkspaceUser,
    reactiveWorkspaceContents,
    setAddQueueSib,
    setContentAreaLoading,
    setFamilyAddedProductListing,
    setFamilyAddedProductTypeData,
    setIsAddOnQueue,
    setIsAddOngoing,
    setIsAddProhibited,
    setIsWorkspaceEditable,
    setReactiveWorkspaceContents,
    setUpdatedDtv,
    setWorkspaceContentItems,
    setWorkspaceContentItemsViaNugget,
    setWorkspaceDTV,
    setWorkspaceHasError,
    setWorkspacePTPs,
    setWorkspaceTab,
    updatePartDTV,
    updateWorkspaceContents,
    workspaceContents,
    workspaceHasError,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { setWorkspaceLoadingState } from 'api/graphQL/reactiveVars/workspaceLoadingState'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import {
    setAreSharedPropertiesVisible,
    workspaceToggles,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import {
    ContentInsertVariables,
    ExtendedContentInsertVariable,
} from 'api/graphQL/types/contentInsert.types'
import {
    AddProjectContentItemInputResponse,
    RemoveProjectContentItemInputResponse,
} from 'api/graphQL/types/productFamilyTypes'
import {
    GetNuggetProductTypePropertiesViewResponse,
    GetNuggetResponse,
    GetNuggetResponseWithImportedData,
    GetProductFamilyDocumentTemplateViewResponse,
    GetProductFamilyResponse,
    GetProjectNuggetProductTypePropertiesViewResponse,
    GetProjectNuggetResponse,
    GetProjectNuggetResponseForImportedNugget,
    GetProjectProductFamilyDocumentTemplateViewResponse,
    GetProjectProductFamilyElementMetadaResponse,
    GetProjectProductFamilyResponse,
} from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import { WorkspaceEditor } from 'components/WorkspaceEditor/WorkspaceEditor'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { AddProductTypeInput } from 'models/addProductTypeInput'
import { NewProjectElementNodeResult } from 'models/newProjectElementNodeResult'
import {
    GetOMProjectElementNodesResponseForWorkspace,
    GetProjectElementNodesResponse,
} from 'models/projectElementNode'
import { ProjectElementMetadata } from 'models/projectElements'
import { ProjectSettings, UnitsOfMeasure } from 'models/projectSettings'
import { ProjectProductFamilyWithTable } from 'models/specificationSection'
import React from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { useBetaWorkspaceFlag } from 'shared/useBetaWorkspaceFlag'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    HandleContentItemUpdateParams,
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WORKSPACE_NOTE_COUNT_UPDATE,
    WorkspaceManufacturerSelected,
    WorkspaceNotesCountUpdate,
    filteredProductList,
    getFromLocalStorage,
    mergeAndSortProducts,
    processManufacturerServiceWorkerData,
    updateContentItemHiddenStatus,
} from 'shared/utils/workspaceUtils'
import { AreaProvider } from './Common/AreaContext'
import { getUpdatedElements } from './Common/ContentItemElementText'
import {
    AddArticleErrorMessage,
    AddPropertyErrorMessage,
    KeyErrorMessage,
    KeyGenerationSuccessMessage,
    validateAddInput,
} from './Common/addUtils'
import { useCustomTransition } from './Common/hooks/useCustomTransition'
import {
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
} from './Common/model'
import {
    WorkspaceTabTitles,
    getContentOwnerType,
    mergeProjectNuggets,
    reduceProjectNuggetPtps,
} from './Common/utils'
import FamilyArea from './FamilyArea'
import HtmlParagraphPasteProgressDialog from './HtmlParagraphPasteProgressDialog'
import PropertiesArea from './PropertiesArea'
import ReviewAssemblyComponent from './ReviewAssemblyComponent'
import { WorkspaceActionBarContext } from './WorkspaceActionBarContext'
import WorkspaceContentAreaHeader from './WorkspaceContentAreaHeader'
import {
    ContentItemWorker,
    WorkspaceContentProvider,
} from './WorkspaceContentContext'
import { RighMostPane } from './WorkspaceRightPane/RightMostPane'
import { RightMostPaneBeta } from './WorkspaceRightPane/RightMostPaneBeta'
import { ToggleHandlerType } from './WorkspaceRightPane/SidebarToggles'
import { useShowRightMostPane } from './WorkspaceRightPane/context/RightMostPaneContext'
import { RightMostPaneTabs } from './WorkspaceRightPane/enums/RightMostPaneTabs'
import type { TextVariant } from './constants'

// Families with more than this many elements will have articles collapsed by default
const OM_LARGE_FAMILY_THRESHOLD = 500

export interface WorkspaceContentAreaStylesProps {
    elementType?: string
    isContentGroup?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}

export const useWorkspaceContentAreaStyles = makeStyles((theme: Theme) => ({
    formArea: {
        height: '100%',
        /**
         * CSS here
         */
        '@media (max-width: 1900px)': {
            marginRight: '2%',
            marginLeft: '2%',
        },
        '@media (min-width: 1900px)': {
            marginRight: (props: WorkspaceContentAreaStylesProps) =>
                props.isBetaWorkspaceToggleEnabled
                    ? '5%'
                    : props.elementType === 'PRODUCTFAMILY' ||
                        props.elementType === 'PRODUCTTYPE' ||
                        props.elementType === 'ASSEMBLY' ||
                        props.isContentGroup
                        ? 0
                        : theme.spacing(2),
            marginLeft: (props: WorkspaceContentAreaStylesProps) =>
                props.isBetaWorkspaceToggleEnabled ? '5%' : 0,
        },
        marginBottom: (props: WorkspaceContentAreaStylesProps) =>
            props.elementType === 'PRODUCTFAMILY' ||
                props.elementType === 'PRODUCTTYPE' ||
                props.elementType === 'ASSEMBLY' ||
                props.isContentGroup
                ? 0
                : theme.spacing(2),
        '& .spectable-loading': {
            marginBottom: -theme.spacing(2.5),
        },
    },
    formSidebar: {
        minWidth: pxToRem(216),
        maxWidth: pxToRem(216),
        '@media (max-width: 1024px)': {
            minWidth: pxToRem(180),
            maxWidth: pxToRem(180),
        },
    },
    container: {
        position: 'relative',
    },
    fullHeight: {
        height: '100%',
    },
    formAreaContainer: {
        position: 'absolute',
        top: (props: WorkspaceContentAreaStylesProps) =>
            props.isBetaWorkspaceToggleEnabled ? 0 : 80,
        bottom: 0,
        paddingRight: theme.spacing(1),
        flexWrap: 'nowrap',
        padding: theme.spacing(2),
    },
    workspaceFormAreaContainer: {
        padding: 0,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    propertiesPane: {
        backgroundColor: '#FFFFFF',
        paddingTop: pxToRem(5),
        paddingBottom: pxToRem(5),
        width: `${pxToRem(305)}!important`,
        transition: 'width 330ms ease-in-out',
    },
    askDelaWidth: {
        width: `${pxToRem(400)} !important`, // make important to override width of propertiesPane
    },
    panel: {
        overflowX: 'hidden',
        transition: '100ms',
        zIndex: 1,
        '& *::-webkit-scrollbar': {
            width: 6,
            height: 20,
        },
        '& *::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '& *::-webkit-scrollbar-track': {
            boxShadow: 'none',
        },
    },

    propertyPanel: {
        transition: '100ms',
        zIndex: 1,
        '& *::-webkit-scrollbar': {
            width: 6,
            height: 20,
        },
        '& *::-webkit-scrollbar-thumb': {
            backgroundColor: '#464646',
            borderRadius: 100,
        },
        '& *::-webkit-scrollbar-track': {
            boxShadow: 'none',
        },
        maxWidth: pxToRem(250),
        marginRight: theme.spacing(0.25),
        borderLeft: '1px solid #DADADA',
        minHeight: '100%',
        minWidth: pxToRem(250),
        maxHeight: pxToRem(600),
    },

    collapsedPane: {
        width: 0,
        padding: theme.spacing(0),
        marginLeft: '5%',
    },
    expandedPane: {
        width: 200,
        padding: theme.spacing(1),
        marginLeft: '5%',
    },

    propertyCollapsedPane: {
        visibility: 'hidden',
        opacity: 0,
        width: 0,
    },
    propertyExpandedPane: {},
    expandBtn: {
        position: 'absolute',
        background: theme.palette.common.white,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        padding: '8px 2px',
        alignItems: 'center',
        display: 'flex',
        borderRadius: 5,
        boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
        cursor: 'pointer',
        width: pxToRem(20),
        height: pxToRem(32),
        '& svg': {
            color: '#04325B',
            fontSize: pxToRem(16),
        },
    },
    collapseBtn: {
        transform: 'translate(-50%, -50%) rotate(180deg)',
    },
    collapsedRightPane: (props: WorkspaceContentAreaStylesProps) => ({
        width: `${pxToRem(
            props.isBetaWorkspaceToggleEnabled ? 51 : 36
        )} !important`,
        minWidth: `${pxToRem(
            props.isBetaWorkspaceToggleEnabled ? 51 : 36
        )} !important`,
    }),
}))

export interface WorkspaceContentAreaProps {
    projectId: string
    updateElementList: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[]
    ) => void
    handleUpdatePUCPropertyValue: (elementId: string) => void
    isDataLoading: boolean

    /** Set to true when child elements for shift+click bulk add are being loaded
     * and we want the loading spinner to show.
     */
    isBulkAddDataLoading?: boolean

    selectedElementData?:
    | Partial<ProjectUniformatClassification>
    | Partial<UniformatClassification>
    selectedProjectElementNode: ProjectElementNode | null
    setPropertyValue?: (val: Record<string, string>) => void
    setSelectedSpecAgentIdPt?: (val: string) => void
    propertyValue: Record<string, string>
    isProjectPropertiesVisible: boolean
    isTrackChangesPaneVisible: boolean
    isOnlyAddedToProject?: boolean
    persistState?: (propKey: string, inputValue: boolean | any) => void
    getPersistedState?: (propKey: string) => any
    projectSettings: ProjectSettings
    selectedElementFormat?: string
    allElementsList?: ProjectElementNode[]
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    handleUpdateSelectedTopic?: (
        topic?: Partial<ProductFamilyStudioManagement>
    ) => void
    /** Callback for updating the element list with a new (e.g., duplicate) product type */
    insertNewProductTypeElement: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[],
        isNewPT?: boolean
    ) => void
    handleCallUpdateNameMutation?: () => void
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    setIsDraftMode?: (val: boolean) => void
    selectedSpecAgentIdPt?: string
    updateCategoryElementState: (
        data:
            | GetProjectElementNodesResponse
            | GetOMProjectElementNodesResponseForWorkspace
    ) => void
    updateSelectedNuggetData: (
        data: GetProjectNuggetResponse | undefined
    ) => void
    loadingBackdropProp: boolean
    isElementsPaneDisplayed?: boolean
    refetchSelectedProductsCount?: () => void
}

/**
 * Merges project product family content item nodes.
 * @param contentIdsToReplace - Optional array of content IDs to remove if not in incoming data (e.g., user-added content replaced during BP sync)
 */
export const mergeProjectProductFamilyContentItemNodes = (
    currentValue: GetProjectProductFamilyResponse | undefined,
    newValue: GetProjectProductFamilyResponse | undefined,
    contentIdsToReplace?: string[]
) => {
    // Create a Set of IDs to remove (user-added content replaced by BP)
    const idsToRemove = new Set(contentIdsToReplace || [])

    // Filter out content items that are marked for replacement
    const filteredCurrentContentItemNodes = (
        currentValue?.getProjectProductFamily?.contentItemNodes ?? []
    ).filter((item) => {
        // If item ID is marked for removal and not in new content, filter it out
        if (item.id && idsToRemove.has(item.id)) {
            const isInNewContent = (
                newValue?.getProjectProductFamily?.contentItemNodes ?? []
            ).some((newItem) => newItem.id === item.id)
            return isInNewContent
        }
        return true
    })

    const updatedValue = {
        ...currentValue,
        getProjectProductFamily: {
            ...currentValue?.getProjectProductFamily,
            ...newValue?.getProjectProductFamily,
            contentItemNodes: [
                ...filteredCurrentContentItemNodes,
                ...(newValue?.getProjectProductFamily?.contentItemNodes ?? []),
            ],
            tableList: [
                ...((currentValue?.getProjectProductFamily as ProjectProductFamilyWithTable)
                    ?.tableList ?? []),
                ...((newValue?.getProjectProductFamily as ProjectProductFamilyWithTable)
                    ?.tableList ?? []),
            ],
            familyAddedProductTypes:
                newValue?.getProjectProductFamily.familyAddedProductTypes,
        },
    } as GetProjectProductFamilyResponse

    return updatedValue
}

/**
 * Merges product family content item nodes.
 * @param contentIdsToReplace - Optional array of content IDs to remove if not in incoming data (e.g., user-added content replaced during BP sync)
 */
export const mergeProductFamilyContentItemNodes = (
    currentValue: GetProductFamilyResponse | undefined,
    newValue: GetProductFamilyResponse | undefined,
    contentIdsToReplace?: string[]
) => {
    // Create a Set of IDs to remove (user-added content replaced by BP)
    const idsToRemove = new Set(contentIdsToReplace || [])

    // Filter out content items that are marked for replacement
    const filteredCurrentContentItemNodes = (
        currentValue?.productFamily?.contentItemNodes ?? []
    ).filter((item) => {
        // If item ID is marked for removal and not in new content, filter it out
        if (item.id && idsToRemove.has(item.id)) {
            const isInNewContent = (
                newValue?.productFamily?.contentItemNodes ?? []
            ).some((newItem) => newItem.id === item.id)
            return isInNewContent
        }
        return true
    })

    const updatedValue = {
        ...currentValue,
        productFamily: {
            ...currentValue?.productFamily,
            contentItemNodes: [
                ...filteredCurrentContentItemNodes,
                ...(newValue?.productFamily?.contentItemNodes ?? []),
            ],
            tableList:
                (newValue?.productFamily as Partial<ProductFamilyWithTable>)
                    ?.tableList ??
                (currentValue?.productFamily as Partial<ProductFamilyWithTable>)
                    ?.tableList,
        },
    }
    return updatedValue
}
export const getNuggetForProductType = (
    nuggetData?: Partial<Nugget>,
    selectedNuggetData?: Partial<ProjectNugget>,
    elementId?: string
) => {
    const selectedContentItemNodes = selectedNuggetData?.contentItemNodes
    const contentItemNodes = nuggetData?.contentItemNodes
    if (elementId && elementId === selectedNuggetData?.id) {
        if (
            !(
                selectedContentItemNodes?.[0]?.elements === undefined &&
                selectedContentItemNodes?.length === 1
            )
        ) {
            return selectedNuggetData
        } else {
            if (
                !(
                    contentItemNodes?.[0]?.elements === undefined &&
                    contentItemNodes?.length === 1
                )
            ) {
                return nuggetData
            } else {
                return selectedNuggetData
            }
        }
    } else {
        if (elementId === nuggetData?.id) {
            return nuggetData
        } else {
            return undefined
        }
    }
}

type PartialItems = Partial<ContentItem | DocumentTemplateViewItem>
// Currently works on ContentItem and DocumentTemplateViewItem only
// TODO: remove isContentItem, already managed by "getId()"
export const getUpdatedItemsArray = (
    currentItemsArray: PartialItems[],
    updatedItemsArray: PartialItems[],
    isContentItem: boolean,
    overwrite: boolean = false
) => {
    // cast Item based on "itemId" prop
    const getId = (item: PartialItems) => {
        if ('itemId' in item) {
            return (item as DocumentTemplateViewItem).itemId
        }
        return (item as ContentItem).id
    }

    return updatedItemsArray.reduce(
        (prev, current) => {
            const targetItemIndex = currentItemsArray.findIndex(
                (item) => getId(item) === getId(current)
            )
            if (targetItemIndex >= 0) {
                return prev
                    .slice(0, targetItemIndex)
                    .concat(current)
                    .concat(prev.slice(targetItemIndex + 1))
            } else {
                //if target is not found, add as new content item
                return [...prev, current]
            }
        },
        overwrite
            ? currentItemsArray.filter((item) =>
                updatedItemsArray.find(
                    (updatedItem) => getId(updatedItem) === getId(item)
                )
            )
            : currentItemsArray
    )
}

export const getTabName = (tabIndex: number) => {
    switch (tabIndex) {
        case 0:
            return 'General'
        case 1:
            return 'Products'
        case 2:
            return 'Execution'
    }
}

export const getNewlyAddedItems = (
    updatedContentItemNodes: Partial<ContentItem>[],
    currentContentItemNodes: Partial<ContentItem>[]
) => {
    return updatedContentItemNodes.filter((contentItemNode) => {
        const isAlreadyAdded = currentContentItemNodes.find(
            (existingContentItemNode) =>
                existingContentItemNode.id === contentItemNode.id
        )
        if (!isAlreadyAdded && contentItemNode.textType !== 'PropertyRef') {
            return true
        } else {
            return false
        }
    })
}

export const removeOutdatedDTVContentItem = (
    dtv: Partial<DocumentTemplateView>,
    idYoRemove: string
) => {
    return dtv?.items?.filter((item) =>
        item?.documentTemplateViewContents?.find((dtvContents) =>
            dtvContents?.documentTemplateViewContentItems?.find(
                (dtvChildContent) => dtvChildContent?.id !== idYoRemove
            )
        )
    )
}

const WorkspaceContentArea: React.FC<WorkspaceContentAreaProps> = ({
    projectId,
    updateElementList,
    persistState,
    getPersistedState,
    handleUpdatePUCPropertyValue,
    isDataLoading,
    isBulkAddDataLoading,
    selectedElementData,
    selectedProjectElementNode,
    setPropertyValue,
    setSelectedSpecAgentIdPt,
    propertyValue,
    projectSettings,
    isOnlyAddedToProject,
    isProjectPropertiesVisible: toggleClass,
    isTrackChangesPaneVisible,
    insertNewProductTypeElement,
    allElementsList,
    selectedElementFormat,
    contentTeamFromStudioPage,
    isDraftMode,
    setIsDraftMode,
    selectedSpecAgentIdPt,
    selectedTopic,
    handleUpdateSelectedTopic,
    updateCategoryElementState,
    updateSelectedNuggetData,
    loadingBackdropProp,
    isElementsPaneDisplayed,
    refetchSelectedProductsCount,
}) => {
    const isProductTypeElement = React.useMemo(() => {
        return selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
    }, [selectedProjectElementNode])

    const isFamilyElement = React.useMemo(
        () => selectedProjectElementNode?.elementType === 'PRODUCTFAMILY',
        [selectedProjectElementNode]
    )

    const [projectNuggetId, setProjectNuggetId] = React.useState<
        string | undefined
    >()

    const [rightMostPaneTab, setRightMostPaneTab] = React.useState<string>('')

    const [
        isMFRSelectedFromListingDialog,
        setisMFRSelectedFromListingDialog,
    ] = React.useState<boolean>(false)
    const [addNewProductType] = useMutation<
        {
            addNewProductType: NewProjectElementNodeResult | null
        },
        { input: AddProductTypeInput }
    >(AddNewProductTypeInputMutation, {
        onCompleted: (data) => {
            setAddNewPt(false)
            if (data?.addNewProductType?.newNode) {
                insertNewProductTypeElement(
                    data?.addNewProductType?.newNode,
                    false,
                    data?.addNewProductType?.newNodesForAdditionalInstances, // Changed this because of new PT flow
                    true
                )
            }
        },
        onError: (error) => {
            setAddNewPt(false)

            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `DuplicateProductTypeError`,
                },
            })
        },
    })
    const addNewProductTypeIntoTree = (
        addProductTypeInput?: AddProductTypeInput
    ) => {
        setAddNewPt(true)

        addProductTypeInput &&
            addNewProductType({
                variables: {
                    input: addProductTypeInput,
                },
            })
    }
    const [index, setIndex] = React.useState<number | undefined>()
    const [indexType, setIndexType] = React.useState<TextVariant | undefined>()
    const [loadingBackdrop, setLoadingBackdrop] = React.useState<boolean>(false)
    // const [showRightMostPane, setShowRightMostPane] = React.useState<boolean>(
    //     true
    // )
    const { showRightMostPane, setShowRightMostPane } = useShowRightMostPane()
    const [expandedArticlesList, setExpandedArticlesList] = React.useState<
        string[]
    >([])
    const [manufacturerArticleIds, setManufacturerArticleId] = React.useState<
        string[] | undefined
    >()
    const { areSharedPropertiesVisible: areSharedVisible } = useReactiveVar(
        workspaceToggles
    )
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const contentTeamProject = useReactiveVar(contentTeamProjectId)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const {
        articleIdToFocus,
        propertyToFocus,
        tabToFocus,
        propertyContentItemId,
    } = useReactiveVar(workspaceProperty)

    const {
        currentUserInfo: { kpUserId },
        isSystemUser,
        isAdmin,
        isPublishingAuthor,
    } = useReactiveVar(userInfo)
    const isStudioManagement = React.useMemo(
        () =>
            isContentTeamUser &&
            ['DraftMasterSpecProject', 'DraftSpecTextProject'].includes(
                projectId
            ) &&
            isSystemUser &&
            (isPublishingAuthor || isAdmin),
        [isContentTeamUser, projectId]
    )
    const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)
    const [_, startTransition] = useCustomTransition()

    const serviceWorker = navigator.serviceWorker
    const contentItemWorker = React.useMemo(() => {
        const worker = new Worker(
            `${process.env.PUBLIC_URL || ''}/workers/contentItemWorker.js`
        ) as ContentItemWorker

        // implement "terminate" method to extend contentItemWorker.js
        worker.terminate = () => {
            // invoke terminate (base from Worker class)
            Worker.prototype.terminate.call(worker)
        }

        return worker
    }, [])

    const updateExpandedArticlesList = React.useCallback(
        (articleId: string) => {
            setExpandedArticlesList((past: string[]) => {
                // locate article id from list
                const articleIdIndex = past.findIndex(
                    (idString) => idString === articleId
                )

                if (articleIdIndex >= 0) {
                    //article id is on the list, collapse it.
                    return past
                        .slice(0, articleIdIndex)
                        .concat(past.slice(articleIdIndex + 1))
                } else {
                    //article id isn't found on the list, expand it.
                    return past.concat([articleId])
                }
            })
        },
        []
    )

    const {
        projectProductFamilyId,
        contentItemNodes,
        dtv,
        isAddProhibited,
        isAddOngoing,
        tabSelected,
        resetOtherTabs,
        isNewAddOnQueue,
        addQueueSib,
        productTypeProperties,
        tableList,
        elementIdOnEdit,
        familyAddedProductTypes,
    } = useReactiveVar(workspaceContents)
    const workspaceError = useReactiveVar(workspaceHasError)
    const { isRemoveNuggetGqlOngoing } = reactiveWorkspaceContents()
    const { currentState, setStateValue } = React.useContext(
        WorkspaceActionBarContext
    )

    const [familySelected, setFamilySelected] = React.useState<{
        elementId?: string | null
        selected?: boolean
        projectId?: string
        familyNodeId?: string
        nodeId?: string
        treePath?: string
    } | null>(null)
    const [productTypeSelected, setProductTypeSelected] = React.useState<
        string | null
    >(null)
    const [generalCalled, setGeneralCalled] = React.useState<boolean>(false)
    const [productsCalled, setProductsCalled] = React.useState<boolean>(false)
    const [executionCalled, setExecutionCalled] = React.useState<boolean>(false)
    const [productFamilyData, setProductFamilyData] = React.useState<
        GetProductFamilyResponse
    >()
    const [addNewPT, setAddNewPt] = React.useState<boolean>(false)
    const [
        projectProductFamilyData,
        setProjectProductFamilyData,
    ] = React.useState<GetProjectProductFamilyResponse>()

    const [nuggetData, setNuggetData] = React.useState<GetNuggetResponse>()
    const [selectedNuggetData, setSelectedNuggetData] = React.useState<
        GetProjectNuggetResponse
    >()

    const [familyItemsExpanded, setFamilyItemsExpanded] = React.useState<
        boolean
    >(false)

    const [
        productTypeItemsExpanded,
        setProductTypeItemsExpanded,
    ] = React.useState<boolean>(false)

    const [isPasteInProgress, setIsPasteInProgress] = React.useState<boolean>(
        false
    )

    const initialNotesVisibleValue = React.useMemo(() => {
        const value = getFromLocalStorage(
            kpUserId,
            projectId!,
            `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isNotesVisible`
        )
        return value
    }, [kpUserId, selectedProjectElementNode, projectId])

    const initialPropertyVisibleValue = React.useMemo(() => {
        const value = getFromLocalStorage(
            kpUserId,
            projectId!,
            `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isPropertyVisible`
        )
        return value
    }, [kpUserId, selectedProjectElementNode, projectId])

    const [isElementNotesVisible, setIsElementNoteVisible] = React.useState<
        boolean
    >(initialNotesVisibleValue)
    const [
        isElementPropertiesVisible,
        setIsElementPropertiesVisible,
    ] = React.useState<boolean>(initialPropertyVisibleValue)
    // hook to check if the beta workspace is enabled using url params
    const { isBetaWorkspaceEnabled } = useBetaWorkspaceFlag()
    const { previewFeatures } = usePreviewFeatures()
    const isBetaWorkspaceToggleEnabled =
        (!contentTeamFromStudioPage || previewFeatures.hasOMWorkspace) &&
        (previewFeatures.hasNewWorkspace2 || isBetaWorkspaceEnabled) &&
        currentState?.showHideStates?.betaWorkspace

    const selectedProductFamily = familySelected?.selected
        ? projectProductFamilyData?.getProjectProductFamily
        : productFamilyData?.productFamily ||
        projectProductFamilyData?.getProjectProductFamily
    const [contentInsertVariables, setContentInsertVariables] = React.useState<
        Partial<ContentInsertVariables>
    >({})

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const previousSelectedElement = usePrevious(selectedProjectElementNode)
    const previousIsBulkAddDataLoading = usePrevious(isBulkAddDataLoading)
    const previousIsBetaWorkspaceToggleEnabled = usePrevious(
        isBetaWorkspaceToggleEnabled
    )

    const [isAddRemoveClicked, setIsAddRemoveClicked] = React.useState(false)
    const [isNuggetRetrieved, setIsNuggetRetrieved] = React.useState(false)

    const [getAssemblyChanges] = useLazyQuery(GetAssebmlyElementChanges, {
        displayName: 'GetAssemblyChanges',
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setElementTrackedChanges(
                data?.getProjectUniformatClassification?.trackChanges
            )
        },
    })
    const [getFamilyChanges] = useLazyQuery(GetFamilyElementChanges, {
        displayName: 'GetFamilyChanges',
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setElementTrackedChanges(
                data?.getProjectProductFamily?.trackChanges
            )
        },
        onError: () => { },
    })

    const [getPTTrackedChanges] = useLazyQuery(GetProductTypeChanges, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setElementTrackedChanges(data?.getProjectNugget?.trackChanges)
        },
    })

    const [
        projectProductTypeProductListingsInfo,
        setProjectProductTypeProductListingsInfo,
    ] = React.useState<
        Partial<GetProjectNuggetResponse> | Partial<GetNuggetResponse>
    >()

    // Track content IDs that should be replaced on next fetch (e.g., user-added content replaced by BP sync)
    const [
        contentIdsToReplaceOnNextFetch,
        setContentIdsToReplaceOnNextFetch,
    ] = React.useState<string[]>([])

    const [
        getProductFamilyDtv,
        { loading: productFamilyDtvLoading },
    ] = useLazyQuery<GetProductFamilyDocumentTemplateViewResponse>(
        ProductFamilyDocumentTemplateViewQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'ProductFamilyDocumentTemplateView',
            onCompleted: (
                data: GetProductFamilyDocumentTemplateViewResponse
            ) => {
                setUpdatedDtv(
                    data?.productFamily?.documentTemplateView as Partial<
                        DocumentTemplateView
                    >
                )
            },
        }
    )

    /**
     * Inserts a new 'pill' into the 'elements' array at a specific index.
     * As per requirement we want to create the pill component after mutating contentItemNodes for SMM is received from API.
     * i.e.: User adds a paragraph and then immediately inserts a pill (via hotkey or action button)
     * @param {Partial<ContentItem>[]} mutatedContentItemNodes - The contentItemNodes to update.
     */
    const insertPillElementAtIndex = React.useCallback(
        (mutatedContentItemNodes: Partial<ContentItem>[]) => {
            const newItems = getNewlyAddedItems(
                mutatedContentItemNodes,
                contentItemNodes ?? []
            )

            if (contentTeamFromStudioPage && index!! >= 0 && indexType) {
                const updatingElementsForPills = newItems?.[0]?.elements?.[0]

                if (newItems && newItems.length) {
                    newItems[0].elements = getUpdatedElements({
                        fieldStateId: updatingElementsForPills?.id,
                        fieldStateText: updatingElementsForPills?.text,
                        positionIndex: index,
                        type: indexType, // TODO: create string literals type
                        fieldStateChangeType: 'ADDED',
                    }) as Partial<TextContentElement>[]
                }
                setIndexType(undefined)
                setIndex(undefined)
            }

            return newItems
        },
        [contentTeamFromStudioPage, index, indexType]
    )

    /**
     * Updates 'addQueueSib' and 'isNewAddOnQueue' based on the new content items.
     * Clears the text of the newly added paragraph if 'isNewAddOnQueue' is true.
     *
     * @param {Partial<ContentItem>[]} contentItemNodesToQueue - The contentItemNodes to queue.
     */
    const manageAddOnQueue = React.useCallback(
        (
            contentItemNodesToQueue: Partial<ContentItem>[],
            newItems?: Partial<ContentItem>[]
        ) => {
            if (isNewAddOnQueue && !addQueueSib) {
                setAddQueueSib(newItems?.[0].id)
            } else if (isNewAddOnQueue && !!addQueueSib) {
                if (addQueueSib?.[0] !== '' && addQueueSib?.length === 1) {
                    setIsAddOnQueue(false)
                    setAddQueueSib(undefined, true)
                } else {
                    setAddQueueSib(contentItemNodesToQueue[0].id, true)
                }
            } else {
                if (isNewAddOnQueue) {
                    setIsAddOnQueue(false)
                }
                if (!!addQueueSib) {
                    setAddQueueSib(undefined, true)
                }
            }
            if (isNewAddOnQueue) {
                newlyAddedParagraphText('')
            }
        },
        [isNewAddOnQueue, addQueueSib]
    )

    const [
        addNewPropertyInNugget,
        { error: addNewPropertyInNuggetError },
    ] = useMutation<AddProjectContentItemInputResponse>(
        AddNewProductTypeContentItem(6),
        {
            fetchPolicy: 'no-cache',
            onCompleted: (data: AddProjectContentItemInputResponse) => {
                const updatedContentItemsWithPill = insertPillElementAtIndex(
                    data?.addProjectContentItem?.contentItemNodes ?? []
                )
                const newItems = getNewlyAddedItems(
                    data?.addProjectContentItem?.contentItemNodes as Partial<
                        ContentItem
                    >[],
                    contentItemNodes as Partial<ContentItem>[]
                )
                manageAddOnQueue(updatedContentItemsWithPill, newItems)
                handleContentItemUpdate({
                    updatedContentItems: updatedContentItemsWithPill ?? [],
                    projectNuggets: data.addProjectContentItem.projectNuggets,
                    updatedDtv:
                        data?.addProjectContentItem?.projectNuggets?.[0]
                            ?.projectProductTypePropertiesView,
                })
                // update Project Nugget Product Type Properties with
                // newly-added item
                setProjectProductTypeProductListingsInfo((past) => {
                    const pastValue = { ...past } as Partial<
                        GetProjectNuggetResponse
                    >
                    const updatedProjectNuggets = mergeProjectNuggets(
                        [pastValue?.getProjectNugget ?? {}],
                        data?.addProjectContentItem.projectNuggets ?? []
                    )[0]

                    setWorkspacePTPs(
                        [updatedProjectNuggets!] as Partial<ProjectNugget>[],
                        updatedProjectNuggets.projectProductTypeProperties,
                        ProjectElementType.PRODUCTTYPE
                    )
                    const result = {
                        ...pastValue,
                        getProjectNugget: {
                            ...updatedProjectNuggets,
                        },
                    } as typeof past

                    return result
                })
                // Don't update the workspaceContents reactive variable nuless we need to--prevents extra renders
                if (isAddProhibited || isAddOngoing) {
                    updateWorkspaceContents({
                        isAddProhibited: false,
                        isAddOngoing: false,
                    })
                }
                showGlobalSnackbar({
                    variables: {
                        message: 'Changes Saved Successfully',
                        variant: 'success',
                        id: `PropertiesArea-AddNewContentItemSuccess`,
                    },
                })
                retrieveUpdatedElementChanges()
            },
            onError: (error) => {
                setWorkspaceHasError(true)
                showGlobalSnackbar({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: `PropertiesArea-AddNewContentItem`,
                    },
                })
            },
        }
    )

    const [addNewProperty, { error: addNewPropertyError }] = useMutation<
        AddProjectContentItemInputResponse,
        {
            input: Partial<AddProjectContentItemInput>
            partType: string
            productFamilyId: string
            rootArticleContentItemIds: string[]
        }
    >(AddNewContentItem(6), {
        fetchPolicy: 'no-cache',
        onCompleted: (data: AddProjectContentItemInputResponse) => {
            const updatedContentItemsWithPill = insertPillElementAtIndex(
                data?.addProjectContentItem?.contentItemNodes ?? []
            )
            const newItems = getNewlyAddedItems(
                data?.addProjectContentItem?.contentItemNodes as Partial<
                    ContentItem
                >[],
                contentItemNodes as Partial<ContentItem>[]
            )
            manageAddOnQueue(updatedContentItemsWithPill, newItems)
            // To save re-renders, update all relevant parts of workspaceContents reactive variable at once.
            handleContentItemUpdate({
                updatedContentItems: updatedContentItemsWithPill,
                projectNuggets: data?.addProjectContentItem?.projectNuggets,
                updatedDtv: data?.addProjectContentItem
                    ?.documentTemplateView as DocumentTemplateView,
                mergeDtv: true,
                newIsAddOngoing: false,
                newIsAddProhibited: false,
            })
            updateWorkspaceContents({
                isAddProhibited: false,
                isAddOngoing: false,
            })
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `FamilyArea-AddNewContentItemSuccess`,
                },
            })
            retrieveUpdatedElementChanges()
        },
        onError: (error) => {
            setWorkspaceHasError(true)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `FamilyArea-AddNewContentItem`,
                },
            })
        },
    })

    const [addSustainabilityTopic] = useMutation<
        AddProjectContentItemInputResponse,
        {
            input: Partial<AddProjectContentItemInput>
            projectId: string
            partType: string
            productFamilyId: string
            rootArticleContentItemIds: string[]
        }
    >(AddNewSustainabilityTopic(6), {
        fetchPolicy: 'no-cache',
        onCompleted: (data: AddProjectContentItemInputResponse) => {
            const updatedContentItemsWithPill = insertPillElementAtIndex(
                data?.addProjectContentItem?.contentItemNodes ?? []
            )
            manageAddOnQueue(updatedContentItemsWithPill)
            // To save re-renders, update all relevant parts of workspaceContents reactive variable at once.
            handleContentItemUpdate({
                updatedContentItems: updatedContentItemsWithPill,
                projectNuggets: data?.addProjectContentItem?.projectNuggets,
                updatedDtv: data?.addProjectContentItem
                    ?.documentTemplateView as DocumentTemplateView,
                mergeDtv: true,
                newIsAddOngoing: false,
                newIsAddProhibited: false,
            })
            updateWorkspaceContents({
                isAddProhibited: false,
                isAddOngoing: false,
            })
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `FamilyArea-AddNewContentItemSuccess`,
                },
            })
            retrieveUpdatedElementChanges()
        },
        onError: (error) => {
            setWorkspaceHasError(true)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `FamilyArea-AddNewContentItem`,
                },
            })
        },
    })

    const handleKeyValidation = React.useCallback(
        (
            textType: string,
            propertyKey?: string,
            isProductGroup?: boolean,
            siblingIds?: string[]
        ): string | undefined => {
            const isForArticle = textType === 'Article'
            return validateAddInput(
                propertyKey ?? '',
                isForArticle,
                contentItemNodes?.filter(
                    (contentItem) =>
                        !!siblingIds?.find(
                            (siblingId) => siblingId === contentItem.id
                        )
                )
            )
                ? isForArticle
                    ? AddArticleErrorMessage
                    : AddPropertyErrorMessage
                : undefined
        },
        [contentItemNodes]
    )

    const handleAddNewProperty = React.useCallback(
        (
            inputValues: Partial<AddProjectContentItemInput>,
            rootArticleContentItemIds?: string[],
            siblingIds?: string[],
            isProductGroup?: boolean,
            indexOfPillForNewPt?: number,
            typeOfPillForNewPt?: TextVariant,
            isPillWithNewPara?: boolean,
            productTypeNodeInFamily?: Partial<ProjectElementNode>
        ) => {
            if (isAddOngoing) {
                return
            }
            if (indexOfPillForNewPt!! >= 0 && typeOfPillForNewPt) {
                setIndexType(typeOfPillForNewPt)
                setIndex(indexOfPillForNewPt)
            }
            const errorMessage = handleKeyValidation(
                inputValues?.textType!,
                inputValues?.propertyKey,
                isProductGroup,
                siblingIds
            )
            if (errorMessage && errorMessage !== '') {
                setWorkspaceHasError(true)
                setIndexType(undefined)
                setIndex(undefined)
                showGlobalSnackbar({
                    variables: {
                        message: errorMessage,
                        variant: 'error',
                        id: `AddPropertyViolation`,
                    },
                })
            } else {
                if (workspaceError) {
                    setWorkspaceHasError(false)
                }
                delete inputValues.uiIndex

                const addApiCall =
                    selectedProjectElementNode?.elementType ===
                        ProjectElementType.PRODUCTTYPE
                        ? addNewPropertyInNugget
                        : addNewProperty

                const productTypeSpecificVariables = {
                    ...inputValues,
                    projectNuggetId: !!productTypeNodeInFamily?.elementId
                        ? productTypeNodeInFamily.elementId
                        : selectedProjectElementNode?.elementId
                            ? (projectProductTypeProductListingsInfo as Partial<
                                GetProjectNuggetResponse
                            >)?.getProjectNugget?.id
                            : (projectProductTypeProductListingsInfo as Partial<
                                GetNuggetResponse
                            >)?.nugget?.id,
                    projectId: dtv?.projectId,
                    documentTemplateViewId: dtv?.id,
                    productTypePropertyType:
                        inputValues.productTypePropertyType ?? 'Specific',
                }

                addApiCall({
                    variables: {
                        input:
                            selectedProjectElementNode?.elementType ===
                                ProjectElementType.PRODUCTTYPE ||
                                !!productTypeNodeInFamily
                                ? productTypeSpecificVariables
                                : {
                                    ...inputValues,
                                    partType: selectedTopic
                                        ? 'CONTENTGROUP'
                                        : inputValues?.partType,
                                    productTypePropertyType:
                                        inputValues.productTypePropertyType ??
                                        'Shared',
                                },
                        projectId: inputValues?.projectId,
                        nodeId: !!productTypeNodeInFamily?.id
                            ? productTypeNodeInFamily.id
                            : selectedProjectElementNode?.elementType ===
                                ProjectElementType.PRODUCTTYPE
                                ? selectedProjectElementNode.id ?? ''
                                : undefined,
                        partType: selectedTopic
                            ? 'CONTENTGROUP'
                            : inputValues.partType?.toUpperCase() ?? '',
                        productFamilyId: selectedTopic
                            ? selectedTopic?.ppfId!
                            : (inputValues.projectProductFamilyId as string) ??
                            '',
                        rootArticleContentItemIds:
                            rootArticleContentItemIds ?? [],
                        isTrackChangeToggleOn: isTrackChangeToggleOn,
                    },
                })
                    .then((response) => {
                        if (response?.errors) {
                            const responseMessage = response?.errors.toString()
                            const errorMessage = responseMessage
                                .toLowerCase()
                                .includes('key is not unique')
                                ? KeyErrorMessage
                                : responseMessage

                            showGlobalSnackbar({
                                variables: {
                                    message: errorMessage,
                                    variant: 'error',
                                    id: 'PropertiesArea-AddNewContentItem',
                                },
                            })
                            return
                        }

                        if (
                            !workspaceError ||
                            !addNewPropertyInNuggetError ||
                            !addNewPropertyError
                        ) {
                            if (
                                inputValues?.propertyKey &&
                                inputValues?.propertyKey !== '' &&
                                inputValues?.textType !== 'Article'
                            ) {
                                showGlobalSnackbar({
                                    variables: {
                                        message: KeyGenerationSuccessMessage,
                                        variant: 'success',
                                        id: 'AddPropertyWithKeySuccess',
                                    },
                                })
                            }
                        }
                    })
                    .catch((error) => {
                        showGlobalSnackbar({
                            variables: {
                                message: error.message,
                                variant: 'error',
                                id: `PropertiesArea-AddNewContentItem`,
                            },
                        })
                    })
            }
        },
        [
            workspaceError,
            addNewPropertyInNugget,
            showGlobalSnackbar,
            addNewProperty,
            selectedProjectElementNode,
            handleKeyValidation,
            dtv,
            projectProductTypeProductListingsInfo,
            addNewPropertyInNuggetError,
            addNewPropertyError,
            isTrackChangeToggleOn,
            isAddOngoing,
        ]
    )

    const handleAddSustainabilityTopic = React.useCallback(
        (
            template: NormalizedDocumentTemplateViewItem,
            parent: NormalizedDocumentTemplateViewItem,
            topicIds: string[]
        ) => {
            const contentItemParentType = parent?.type ?? 'Nugget'

            const contentItemOwnerType = getContentOwnerType(
                parent as NormalizedChildDocumentTemplateViewItem,
                template as NormalizedDocumentTemplateViewItem
            )

            const contentItemOwnerId =
                contentItemParentType === 'Nugget' ? undefined : parent?.id

            const currentInput = {
                contentItemOwnerType: contentItemOwnerType,
                contentItemParentId: parent?.id,
                contentItemParentType: contentItemParentType,
                contentItemOwnerId: contentItemOwnerId,
                index:
                    (template?.paragraphOrder as number) > 0
                        ? (template?.paragraphOrder as number) - 1
                        : (template?.order as number) > 0 // if paragraph order is undefined, then we consider order
                            ? (template?.order as number)
                            : 0,
                partType: parent?.partType?.toUpperCase() ?? '',
                textType: 'Paragraph',
                productTypePropertyType: 'Shared',
                projectId: projectId!,
                projectProductFamilyId:
                    projectProductFamilyId! ??
                    workspaceContents().projectProductFamilyId,
                topicIds: topicIds!,
                documentTemplateViewId: dtv?.id,
            } as Partial<AddProjectContentItemInput>

            addSustainabilityTopic({
                variables: {
                    input: currentInput,
                    partType: parent?.partType?.toUpperCase() ?? '',
                    projectId: projectId!,
                    productFamilyId:
                        projectProductFamilyId! ??
                        workspaceContents().projectProductFamilyId,
                    rootArticleContentItemIds: [parent?.articleId!],
                },
            })
        },
        [addSustainabilityTopic, dtv, projectId, projectProductFamilyId]
    )

    const handleEditKeyChanges = React.useCallback(
        (
            textType: string,
            propertyKey?: string,
            siblingContents?: string[]
        ): boolean => {
            return !!handleKeyValidation(
                textType,
                propertyKey,
                undefined,
                siblingContents
            )
        },
        [handleKeyValidation]
    )

    const [
        getProjectProductFamilyDtv,
        { loading: projectProductFamilyDtvLoading },
    ] = useLazyQuery<GetProjectProductFamilyDocumentTemplateViewResponse>(
        ProjectProductFamilyDocumentTemplateViewQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'ProductFamilyDocumentTemplateView',
            onCompleted: React.useCallback(
                (data: GetProjectProductFamilyDocumentTemplateViewResponse) => {
                    setUpdatedDtv(
                        data.getProjectProductFamily.documentTemplateView
                    )
                },
                []
            ),
        }
    )

    const [
        getSelectedProjectProductFamilyDtv,
        { loading: selectedProjectProductFamilyDtvLoading },
    ] = useLazyQuery<GetProjectProductFamilyDocumentTemplateViewResponse>(
        ProjectProductFamilyDocumentTemplateViewQuery,
        {
            fetchPolicy: 'no-cache',
            displayName: 'SelectedProductFamilyDocumentTemplateView',
            onCompleted: React.useCallback(
                (data: GetProjectProductFamilyDocumentTemplateViewResponse) => {
                    setWorkspaceDTV(
                        data.getProjectProductFamily.documentTemplateView
                    )
                },
                []
            ),
        }
    )

    const updateTabVisitStates = React.useCallback(
        (selectedTabIndex: number, newValue: boolean = true) => {
            switch (selectedTabIndex) {
                case 0:
                    setGeneralCalled(newValue)
                    break
                case 1:
                    setProductsCalled(newValue)
                    break
                case 2:
                    setExecutionCalled(newValue)
                    break
            }
        },
        []
    )

    const [getProductFamily, { loading: productFamilyLoading }] = useLazyQuery<
        GetProductFamilyResponse
    >(ProductFamilyQuery(6), {
        fetchPolicy: 'no-cache',
        displayName: 'ProjectProductFamily',
        onCompleted: React.useCallback(
            (data: GetProductFamilyResponse) => {
                updateTabVisitStates(tabSelected)
                const newValue = mergeProductFamilyContentItemNodes(
                    productFamilyData,
                    data,
                    contentIdsToReplaceOnNextFetch
                )

                // Clear contentIdsToReplaceOnNextFetch after it has been used
                if (
                    contentIdsToReplaceOnNextFetch &&
                    contentIdsToReplaceOnNextFetch.length > 0
                ) {
                    setContentIdsToReplaceOnNextFetch([])
                }

                setProductFamilyData((past) => {
                    return newValue
                })
                setFamilyAddedProductTypeData()
                setWorkspaceContentItemsViaNugget(undefined, newValue)
                setLoadingBackdrop(false)
                setIsAddRemoveClicked(false)
            },
            [
                tabSelected,
                updateTabVisitStates,
                productFamilyData,
                contentIdsToReplaceOnNextFetch,
            ]
        ),
        onError: (error) => {
            setLoadingBackdrop(false)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_ProjectProductFamily',
                },
            })
        },
    })

    const [deleteProperty] = useMutation(RemoveProjectContentItemMutation(6), {
        fetchPolicy: 'no-cache',
        onCompleted: (data: RemoveProjectContentItemInputResponse) => {
            startTransition(() =>
                updatePartDTV(
                    data?.removeProjectContentItem
                        ?.documentTemplateView as DocumentTemplateView,
                    dtv!
                )
            )
            startTransition(() => {
                const updatedContentItems = getUpdatedItemsArray(
                    contentItemNodes ?? [],
                    data?.removeProjectContentItem?.contentItemNodes ??
                    ([] as Partial<ContentItem>[]),
                    true,
                    false
                )

                contentItemWorker.postMessage({
                    dtv: dtv?.items ?? [],
                    selectedTab: WorkspaceTabTitles[tabSelected] as string,
                    workspaceContentItems: contentItemNodes ?? [],
                })
                contentItemWorker.onmessage = (event: MessageEvent) => {
                    setWorkspaceContentItems(
                        updatedContentItems.concat(event.data.otherTabContents)
                    )
                }
            })
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `FamilyArea-RemoveContentItemSuccess`,
                },
            })
            retrieveUpdatedElementChanges()
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `FamilyArea-RemoveContentItem`,
                },
            })
        },
    })

    const [deleteProductTypeProperty] = useMutation<
        RemoveProjectContentItemInputResponse
    >(RemoveProductTypeContentItem(6), {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setWorkspaceDTV(
                data?.removeProjectContentItem?.projectNuggets?.[0]
                    .projectProductTypePropertiesView
            )

            startTransition(() => {
                setProjectProductTypeProductListingsInfo((past) => {
                    const pastValue = past as Partial<GetProjectNuggetResponse>
                    const result = {
                        ...pastValue,
                        getProjectNugget: {
                            ...reduceProjectNuggetPtps(
                                [pastValue.getProjectNugget ?? {}],
                                data.removeProjectContentItem.projectNuggets ??
                                []
                            )[0],
                        },
                    }
                    setWorkspacePTPs(
                        [result?.getProjectNugget!] as Partial<ProjectNugget>[],
                        undefined,
                        ProjectElementType.PRODUCTTYPE
                    )
                    return result
                })
            })

            startTransition(() => {
                const updatedContentItems = getUpdatedItemsArray(
                    contentItemNodes ?? [],
                    data?.removeProjectContentItem?.contentItemNodes ??
                    ([] as Partial<ContentItem>[]),
                    true,
                    false
                )

                contentItemWorker.postMessage({
                    dtv: dtv?.items ?? [],
                    selectedTab: WorkspaceTabTitles[tabSelected] as string,
                    workspaceContentItems: contentItemNodes ?? [],
                })
                contentItemWorker.onmessage = (event: MessageEvent) => {
                    setWorkspaceContentItems(
                        updatedContentItems.concat(event.data.otherTabContents)
                    )
                }
            })

            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `PropertiesArea-RemoveContentItemSuccess`,
                },
            })
            retrieveUpdatedElementChanges()
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: `PropertiesArea-RemoveContentItem`,
                },
            })
        },
    })

    const [getNuggetProductTypePropertiesView] = useLazyQuery<
        GetNuggetProductTypePropertiesViewResponse
    >(NuggetProductTypePropertiesViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (data: GetNuggetProductTypePropertiesViewResponse) => {
            if (
                !selectedProjectElementNode?.elementId &&
                selectedProjectElementNode?.baseElementId === data?.nugget?.id
            ) {
                setWorkspaceDTV(
                    data?.nugget?.productTypePropertiesView,
                    data?.nugget?.productListings
                )
            }
            setNuggetData(undefined)
            setWorkspaceDTV(
                data?.nugget?.productTypePropertiesView as Partial<
                    DocumentTemplateView
                >,
                data?.nugget?.productListings ?? []
            )
            setWorkspaceContentItems([])

            getNugget({
                variables: {
                    id: selectedProjectElementNode?.baseElementId,
                    projectId,
                    productFamilyId:
                        projectProductFamilyId ??
                        data?.nugget?.productTypePropertiesView
                            ?.productFamilyId,
                    partType: WorkspaceTabTitles[tabSelected ?? 0],
                    nodeId: selectedProjectElementNode?.id,
                    rootArticleContentItemIds: expandedArticlesList,
                },
            })

            getProductTypeProductListingsInfo({
                variables: {
                    id: selectedProjectElementNode?.baseElementId,
                    nodeId: selectedProjectElementNode?.id,
                    isTrackChangeToggleOn: isTrackChangeToggleOn,
                },
            })
        },
    })

    const [
        getSelectedProductFamilyWithTable,
        { loading: selectedProductFamilyWithTableLoading },
    ] = useLazyQuery<GetProjectProductFamilyResponse>(
        GetProjectProductFamilyQueryWithTable(6),
        {
            fetchPolicy: 'no-cache',
            displayName: 'GetProjectProductFamily',
            onCompleted: React.useCallback(
                (data: GetProjectProductFamilyResponse) => {
                    updateTabVisitStates(tabSelected)
                    startTransition(() => {
                        const newValue = mergeProjectProductFamilyContentItemNodes(
                            {
                                ...projectProductFamilyData,
                                getProjectProductFamily: {
                                    ...projectProductFamilyData?.getProjectProductFamily,
                                    contentItemNodes: contentItemNodes as Partial<
                                        ContentItem
                                    >[],
                                },
                            },
                            data,
                            contentIdsToReplaceOnNextFetch
                        )

                        // Clear contentIdsToReplaceOnNextFetch after it has been used
                        if (
                            contentIdsToReplaceOnNextFetch &&
                            contentIdsToReplaceOnNextFetch.length > 0
                        ) {
                            setContentIdsToReplaceOnNextFetch([])
                        }

                        setProjectProductFamilyData((past) => newValue)
                        setWorkspaceContentItemsViaNugget(undefined, newValue)

                        setWorkspacePTPs(
                            undefined,
                            (data?.getProjectProductFamily as any)
                                ?.projectProjectTypeProperties,
                            ProjectElementType.PRODUCTFAMILY
                        )
                        setLoadingBackdrop(false)
                        setIsAddRemoveClicked(false)
                        setElementNotesCount(
                            data?.getProjectProductFamily?.notesCount ?? 0
                        )
                    })
                },
                [
                    tabSelected,
                    updateTabVisitStates,
                    projectProductFamilyData,
                    contentItemNodes,
                ]
            ),
            onError: (error) => {
                setLoadingBackdrop(false)
                if (!isBetaWorkspaceToggleEnabled) {
                    showGlobalSnackbar({
                        variables: {
                            message: error.message,
                            variant: 'error',
                            id: 'WorspacePage_GetProjectProductFamily',
                        },
                    })
                }
            },
        }
    )

    const [
        getSelectedProductFamily,
        { loading: selectedProductFamilyLoading },
    ] = useLazyQuery<GetProjectProductFamilyResponse>(
        GetProjectProductFamilyQuery(6),
        {
            fetchPolicy: 'no-cache',
            displayName: 'GetProjectProductFamily',
            onCompleted: (data: GetProjectProductFamilyResponse) => {
                updateTabVisitStates(tabSelected)
                startTransition(() => {
                    const newValue = mergeProjectProductFamilyContentItemNodes(
                        {
                            ...projectProductFamilyData,
                            getProjectProductFamily: {
                                ...projectProductFamilyData?.getProjectProductFamily,
                                contentItemNodes: contentItemNodes as Partial<
                                    ContentItem
                                >[],
                            },
                        },
                        data,
                        contentIdsToReplaceOnNextFetch
                    )

                    // Clear contentIdsToReplaceOnNextFetch after it has been used
                    if (
                        contentIdsToReplaceOnNextFetch &&
                        contentIdsToReplaceOnNextFetch.length > 0
                    ) {
                        setContentIdsToReplaceOnNextFetch([])
                    }

                    if (tabSelected === 1) {
                        // set family added product types when in Product tab only
                        setFamilyAddedProductTypeData(
                            newValue.getProjectProductFamily
                                .familyAddedProductTypes,
                            newValue.getProjectProductFamily
                                .familyProductListings,
                            newValue.getProjectProductFamily
                                .familyCustomManufacturerProducts,
                            newValue.getProjectProductFamily
                                .familyManufacturerImportedManufacturer,
                            newValue.getProjectProductFamily
                                .familyBodImportedManufacturer
                        )
                    }
                    setProjectProductFamilyData(newValue)
                    setWorkspaceContentItemsViaNugget(undefined, newValue)

                    setWorkspacePTPs(
                        undefined,
                        (data?.getProjectProductFamily as any)
                            ?.projectProductTypeProperties,
                        ProjectElementType.PRODUCTFAMILY
                    )
                    setLoadingBackdrop(false)
                    setIsAddRemoveClicked(false)
                    setElementNotesCount(
                        data?.getProjectProductFamily?.notesCount ?? 0
                    )
                    setWorkspaceLoadingState(false)
                })
            },
            onError: (error) => {
                setLoadingBackdrop(false)
                showGlobalSnackbar({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: 'WorspacePage_GetProjectProductFamily',
                    },
                })
            },
        }
    )

    const [getUpdatedFamilyProductListing] = useLazyQuery<
        GetProjectProductFamilyResponse
    >(GetUpdatedProjectProductFamilyProductListingQuery(), {
        fetchPolicy: 'no-cache',
        displayName: 'GetUpdatedFamilyProductListing',
        onCompleted: (data: GetProjectProductFamilyResponse) => {
            if (tabSelected === 1) {
                // set family added product types when in Product tab only
                setFamilyAddedProductListing(
                    data.getProjectProductFamily.familyProductListings,
                    data.getProjectProductFamily
                        .familyCustomManufacturerProducts,
                    data.getProjectProductFamily
                        .familyManufacturerImportedManufacturer,
                    data.getProjectProductFamily.familyBodImportedManufacturer
                )
            }
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_GetUpdatedFamilyProductListing',
                },
            })
        },
    })

    const [getSelectedNuggetProductTypePropertiesView] = useLazyQuery<
        GetProjectNuggetProductTypePropertiesViewResponse
    >(ProjectNuggetProductTypePropertiesViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (
            data: GetProjectNuggetProductTypePropertiesViewResponse
        ) => {
            setSelectedNuggetData((past) => {
                return {
                    ...past,
                    getProjectNugget: {
                        ...past?.getProjectNugget,
                        drawingDesignation:
                            data.getProjectNugget?.drawingDesignation,
                    },
                }
            })
            if (
                selectedProjectElementNode?.elementId ===
                data.getProjectNugget?.id
            ) {
                setWorkspaceDTV(
                    data.getProjectNugget.projectProductTypePropertiesView
                )
            }

            setWorkspaceDTV(
                data.getProjectNugget.projectProductTypePropertiesView,
                data.getProjectNugget.productListings ?? []
            )

            if (
                selectedProjectElementNode?.elementId !== null &&
                isProductTypeElement
            ) {
                setIsNuggetRetrieved(true)
                setWorkspaceContentItems([])
                startTransition(() =>
                    getSelectedNugget({
                        variables: {
                            elementId: selectedProjectElementNode?.elementId,
                            projectId,
                            nodeId: selectedProjectElementNode?.id,
                            productFamilyId:
                                projectProductFamilyId ??
                                data?.getProjectNugget
                                    ?.projectProductTypePropertiesView
                                    ?.productFamilyId,
                            partType: WorkspaceTabTitles[tabSelected ?? 0],
                        },
                    })
                )
            }
        },
    })

    const [retrieveUpdatedPTPView] = useLazyQuery<
        GetProjectNuggetProductTypePropertiesViewResponse
    >(ProjectNuggetProductTypePropertiesViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (
            data: GetProjectNuggetProductTypePropertiesViewResponse
        ) => {
            setWorkspaceDTV(
                data.getProjectNugget
                    .projectProductTypePropertiesView as DocumentTemplateView
            )
        },
    })

    const [retrieveUpdatedFamilyDtv] = useLazyQuery<
        GetProjectProductFamilyDocumentTemplateViewResponse
    >(ProjectProductFamilyDocumentTemplateViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (
            data: GetProjectProductFamilyDocumentTemplateViewResponse
        ) => {
            updatePartDTV(
                data?.getProjectProductFamily
                    ?.documentTemplateView as DocumentTemplateView,
                dtv!,
                isStudioManagement
            )
            if (setLoadingBackdrop) setLoadingBackdrop(false)
        },
    })

    const [retrieveUpdatedFamilyDtvGeneral] = useLazyQuery<
        GetProjectProductFamilyDocumentTemplateViewResponse
    >(ProjectProductFamilyDocumentTemplateViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (
            data: GetProjectProductFamilyDocumentTemplateViewResponse
        ) => {
            startTransition(() =>
                updatePartDTV(
                    data?.getProjectProductFamily
                        ?.documentTemplateView as DocumentTemplateView,
                    dtv!,
                    isStudioManagement
                )
            )
            if (setLoadingBackdrop) setLoadingBackdrop(false)
        },
    })

    const [retrieveUpdatedFamilyDtvProducts] = useLazyQuery<
        GetProjectProductFamilyDocumentTemplateViewResponse
    >(ProjectProductFamilyDocumentTemplateViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (
            data: GetProjectProductFamilyDocumentTemplateViewResponse
        ) => {
            startTransition(() =>
                updatePartDTV(
                    data?.getProjectProductFamily
                        ?.documentTemplateView as DocumentTemplateView,
                    dtv!,
                    isStudioManagement
                )
            )
            if (setLoadingBackdrop) setLoadingBackdrop(false)
        },
    })

    const [retrieveUpdatedFamilyDtvExecution] = useLazyQuery<
        GetProjectProductFamilyDocumentTemplateViewResponse
    >(ProjectProductFamilyDocumentTemplateViewQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'ProductFamilyDocumentTemplateView',
        onCompleted: (
            data: GetProjectProductFamilyDocumentTemplateViewResponse
        ) => {
            startTransition(() =>
                updatePartDTV(
                    data?.getProjectProductFamily
                        ?.documentTemplateView as DocumentTemplateView,
                    dtv!,
                    isStudioManagement
                )
            )
            if (setLoadingBackdrop) setLoadingBackdrop(false)
        },
    })

    const [getSelectedProductFamilyProjectElementMetadata] = useLazyQuery<
        GetProjectProductFamilyElementMetadaResponse
    >(GetProjectProductFamilyElementMetadata, {
        fetchPolicy: 'no-cache',
        displayName: 'GetProjectProductFamily',
        onCompleted: (data) => {
            if (selectedProjectElementNode?.elementType === 'PRODUCTFAMILY') {
                setProjectElementMetadata(
                    data?.getProjectProductFamily
                        .projectElementMetadata as Partial<
                            ProjectElementMetadata
                        >
                )
            }
            setFamilyProjectElementMetadata(
                data?.getProjectProductFamily.projectElementMetadata as Partial<
                    ProjectElementMetadata
                >
            )
        },
        onError: (error) => {
            setLoadingBackdrop(false)
            if (!isBetaWorkspaceToggleEnabled) {
                showGlobalSnackbar({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id:
                            'WorspacePage_GetProjectProductFamilyElementMetadata',
                    },
                })
            }
        },
    })

    const fetchSelectedProductFamilyWithTable = React.useCallback(() => {
        if (familySelected) {
            setExpandedArticlesList([])
            setWorkspaceContentItems([])
            setWorkspaceDTV(null)
            setProductFamilyData(undefined)
            setProjectProductFamilyData(undefined)
            if (!isBetaWorkspaceToggleEnabled) {
                startTransition(
                    () =>
                        getSelectedProductFamilyWithTable({
                            variables: {
                                // Spread familySelected first so its projectId (catalog/MasterSpec
                                // project ID) does not overwrite the workspace projectId below.
                                // familySelected.projectId is the catalog project and is wrong after
                                // a family node is promoted from catalog to project in insertNewProductTypeElement.
                                ...familySelected,
                                elementId: familySelected.elementId,
                                projectId,
                                partType: isStudioManagement
                                    ? undefined
                                    : 'GENERAL',
                            },
                        }),
                    100
                )
            }
        } else {
            console.warn(
                'WorkspaceContentArea: Cannot reload - no family selected'
            )
        }
    }, [
        familySelected,
        projectId,
        isStudioManagement,
        getSelectedProductFamilyWithTable,
    ])
    const handleDeleteProperty = React.useCallback(
        (
            inputValues: Partial<RemoveProjectContentInput>,
            isArticle?: boolean
        ) => {
            const deleteAPICall =
                selectedProjectElementNode?.elementType === 'PRODUCTFAMILY'
                    ? deleteProperty
                    : deleteProductTypeProperty
            deleteAPICall({
                variables: {
                    input: {
                        ...inputValues,
                        projectElementNodeId: selectedTopic
                            ? selectedTopic?.projectElementNodeId
                            : selectedProjectElementNode?.id ?? '',
                        partType: selectedTopic
                            ? 'CONTENTGROUP'
                            : WorkspaceTabTitles[tabSelected ?? 0],
                    },
                    projectId,
                    partType: selectedTopic
                        ? 'CONTENTGROUP'
                        : WorkspaceTabTitles[tabSelected ?? 0],
                    rootArticleContentItemIds:
                        isArticle &&
                            !expandedArticlesList.find(
                                (contentId) =>
                                    contentId === inputValues.contentItemId
                            )
                            ? []
                            : expandedArticlesList,
                    productFamilyId: projectProductFamilyId,
                    nodeId: selectedTopic
                        ? selectedTopic?.projectElementNodeId
                        : selectedProjectElementNode?.elementType ===
                            'PRODUCTTYPE'
                            ? selectedProjectElementNode?.id ?? ''
                            : undefined,
                    isTrackChangeToggleOn: isTrackChangeToggleOn,
                },
            })
        },
        [
            deleteProperty,
            deleteProductTypeProperty,
            selectedProjectElementNode,
            contentItemNodes,
            tabSelected,
            expandedArticlesList,
            projectProductFamilyId,
            isTrackChangeToggleOn,
            selectedTopic,
        ]
    )

    const [getNugget] = useLazyQuery<GetNuggetResponse>(NuggetQuery(6), {
        fetchPolicy: 'no-cache',
        displayName: 'Nugget',
        onCompleted: (data: GetNuggetResponse) => {
            if (data?.nugget !== null) {
                setSelectedSpecAgentIdPt?.(data?.nugget?.specAgentId ?? '')
                const mergedContentItems = getUpdatedItemsArray(
                    contentItemNodes ?? [],
                    data?.nugget?.contentItemNodes ??
                    ([] as Partial<ContentItem>[]),
                    true
                )
                setNuggetData((past) => {
                    const res = {
                        ...past,
                        ...data,
                        nugget: {
                            ...past?.nugget,
                            ...data.nugget,
                            contentItemNodes: mergedContentItems,
                        },
                    }
                    return res
                })
                setWorkspaceContentItems(mergedContentItems)

                updateTabVisitStates(tabSelected)
                setLoadingBackdrop(false)
            }
        },
        onError: (error) => {
            setLoadingBackdrop(false)

            if (!isBetaWorkspaceToggleEnabled) {
                showGlobalSnackbar({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: 'WorspacePage_ProjectNugget',
                    },
                })
            }
        },
    })
    const [getSelectedNugget, { variables }] = useLazyQuery<
        GetProjectNuggetResponse
    >(GetProjectNuggetQuery(6), {
        fetchPolicy: 'no-cache',
        displayName: 'GetProjectNugget',
        onCompleted: (data) => {
            if (data?.getProjectNugget !== null) {
                setSelectedSpecAgentIdPt?.(
                    data?.getProjectNugget?.specAgentId ?? ''
                )
                const mergedContentItems = getUpdatedItemsArray(
                    contentItemNodes as Partial<ContentItem>[],
                    data?.getProjectNugget?.contentItemNodes ?? [],
                    true
                )
                setSelectedNuggetData((past) => {
                    const res = {
                        ...past,
                        ...data,
                        getProjectNugget: {
                            ...past?.getProjectNugget,
                            ...data?.getProjectNugget,
                            contentItemNodes: mergedContentItems,
                        },
                    }
                    return res
                })
                setWorkspaceContentItems(mergedContentItems)
                setProjectElementMetadata(
                    data?.getProjectNugget?.projectElementMetadata as Partial<
                        ProjectElementMetadata
                    >
                )
                updateTabVisitStates(tabSelected)
                setElementNotesCount(data?.getProjectNugget?.notesCount ?? 0)

                //If shared properties are displayed, set familyProjectElementMetadata
                // This is to fix the issue wherein when page reloads it resets the familyProjectMetadata
                if (areSharedVisible) {
                    startTransition(() =>
                        getSelectedProductFamilyProjectElementMetadata({
                            variables: {
                                input: {
                                    elementId: projectProductFamilyId,
                                    projectId,
                                },
                            },
                        })
                    )
                }
            }
            setLoadingBackdrop(false)
            setWorkspaceLoadingState(false)
        },
        onError: (error) => {
            setLoadingBackdrop(false)
            if (
                variables?.input?.elementId ===
                selectedProjectElementNode?.elementId
            ) {
                showGlobalSnackbar({
                    variables: {
                        message: error.message,
                        variant: 'error',
                        id: 'WorspacePage_GetProjectNugget',
                    },
                })
            }
        },
    })

    const [getProjectProductTypeProductListingsInfo] = useLazyQuery<
        Partial<GetProjectNuggetResponseForImportedNugget>
    >(GetProjectProductTypeProductListingsInfo, {
        fetchPolicy: 'no-cache',
        displayName: 'GetProjectNugget',
        onCompleted: (data) => {
            let productListings = filteredProductList(data)
            // Do not merge with custom if SMM
            if (!!contentTeamFromStudioPage) {
                productListings = mergeAndSortProducts(
                    productListings,
                    data?.getProjectNugget?.customManufacturerProducts
                )
            }

            setProjectProductTypeProductListingsInfo((past) => {
                return {
                    getProjectNugget: {
                        ...data?.getProjectNugget,
                        projectProductTypeProperties:
                            data?.getProjectNugget
                                ?.projectProductTypeProperties,
                        productListings,
                    },
                }
            })
            updateWorkspaceContents({ productListings })
            setWorkspacePTPs(
                [data?.getProjectNugget] as Partial<ProjectNugget>[],
                undefined,
                ProjectElementType.PRODUCTTYPE
            )
        },
        onError: (error) => {
            setLoadingBackdrop(false)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_GetProjectNugget',
                },
            })
        },
    })

    const [getUpdatedProductListings] = useLazyQuery<
        Partial<GetProjectNuggetResponseForImportedNugget>
    >(GetProjectNuggetForProductListingsQuery(6), {
        fetchPolicy: 'no-cache',
        displayName: 'GetProjectNugget',

        onCompleted: (data) => {
            setProjectProductTypeProductListingsInfo((past) => {
                const pastProjectNugget = past as Partial<
                    GetProjectNuggetResponseForImportedNugget
                >
                return {
                    getProjectNugget: {
                        ...pastProjectNugget?.getProjectNugget,
                        productListings: filteredProductList(data),
                        customManufacturerProducts:
                            data?.getProjectNugget?.customManufacturerProducts,
                        manufacturerImportedManufacturer:
                            data?.getProjectNugget
                                ?.manufacturerImportedManufacturer,
                        bodImportedManufacturer:
                            data?.getProjectNugget?.bodImportedManufacturer,
                    },
                }
            })

            setWorkspacePTPs(
                [data?.getProjectNugget] as Partial<ProjectNugget>[],
                undefined,
                ProjectElementType.PRODUCTTYPE,
                data?.getProjectNugget?.productListings
            )
            setisMFRSelectedFromListingDialog(false)

            retrieveUpdatedElementChanges()
        },
        onError: (error) => {
            setLoadingBackdrop(false)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_GetProjectNugget',
                },
            })
        },
    })

    const [getProductTypeProductListingsInfo] = useLazyQuery<
        Partial<GetNuggetResponseWithImportedData>
    >(GetProductTypeProductListingsInfo(6), {
        fetchPolicy: 'no-cache',
        displayName: 'GetProjectNugget',
        onCompleted: (data) => {
            if (data?.nugget !== null) {
                setProjectProductTypeProductListingsInfo(data)
            }
            setWorkspacePTPs(
                [data?.nugget] as Partial<Nugget>[],
                undefined,
                ProjectElementType.PRODUCTTYPE
            )
        },
        onError: (error) => {
            setLoadingBackdrop(false)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_GetProjectNugget',
                },
            })
        },
    })

    const [getProjectProductTypeProperties] = useLazyQuery<
        Partial<GetProjectNuggetResponse>
    >(GetProjectProductTypeProperties, {
        fetchPolicy: 'no-cache',
        displayName: 'GetProjectNugget',
        onCompleted: (data) => {
            setProjectProductTypeProductListingsInfo((past) => {
                const pastProjectNugget = past as Partial<
                    GetProjectNuggetResponse
                >
                return {
                    getProjectNugget: {
                        ...pastProjectNugget?.getProjectNugget,
                        projectProductTypeProperties:
                            data?.getProjectNugget
                                ?.projectProductTypeProperties,
                    },
                }
            })

            setWorkspacePTPs(
                [data?.getProjectNugget] as Partial<ProjectNugget>[],
                undefined,
                ProjectElementType.PRODUCTTYPE
            )
        },
        onError: (error) => {
            setLoadingBackdrop(false)
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_GetProjectNugget',
                },
            })
        },
    })

    /**
     * Loading State Coordination (PBI 2503726, Bug 2515739)
     *
     * familyLoading = computed from multiple Apollo query loading states
     * Purpose: Show loading skeleton until ALL required data is fetched
     *
     * Why multiple loading flags?
     * - productFamilyLoading: Main family data (required)
     * - productFamilyDtvLoading: DTV data for rendering (required)
     * - selectedProductFamilyLoading: Selected item details (conditional)
     * - selectedProductFamilyWithTableLoading: Table data (conditional)
     * - projectProductFamilyDtvLoading: Project-specific DTV (conditional)
     * - selectedProjectProductFamilyDtvLoading: Selected project DTV (conditional)
     *
     * Each represents a distinct GraphQL query. All must complete before
     * hiding the skeleton, otherwise users see incomplete/broken UI.
     *
     * Previously used manual coordination (setFamilyLoading) which was error-prone.
     * Now computed automatically from Apollo's built-in loading states (more reliable).
     */
    const familyLoading = React.useMemo(() => {
        return (
            productFamilyLoading ||
            productFamilyDtvLoading ||
            selectedProductFamilyLoading ||
            selectedProductFamilyWithTableLoading ||
            projectProductFamilyDtvLoading ||
            selectedProjectProductFamilyDtvLoading
        )
    }, [
        productFamilyLoading,
        productFamilyDtvLoading,
        selectedProductFamilyLoading,
        selectedProductFamilyWithTableLoading,
        projectProductFamilyDtvLoading,
        selectedProjectProductFamilyDtvLoading,
    ])

    const retrieveUpdatedNugget = React.useCallback(
        (
            isDTVToBeRetrieved: boolean,
            isDrawingTagUpdated?: boolean,
            isSelectedFromListingDialog?: boolean
        ) => {
            setisMFRSelectedFromListingDialog(
                isSelectedFromListingDialog ?? false
            )

            if (
                selectedProjectElementNode?.elementType ===
                ProjectElementType.PRODUCTFAMILY
            ) {
                // Update product listing when in Family view
                if (!!familySelected) {
                    startTransition(() =>
                        getUpdatedFamilyProductListing({
                            variables: {
                                elementId: familySelected.elementId,
                                projectId,
                                partType: 'PRODUCTS',
                                familyNodeId: familySelected?.familyNodeId,
                            },
                        })
                    )
                }
            } else if (selectedProjectElementNode?.elementId !== null) {
                startTransition(() =>
                    getUpdatedProductListings({
                        variables: {
                            input: {
                                elementId:
                                    selectedProjectElementNode?.elementId,
                                projectId,
                            },
                            nodeId: selectedProjectElementNode?.id,
                        },
                    })
                )

                if (isDTVToBeRetrieved) {
                    startTransition(() =>
                        retrieveUpdatedPTPView({
                            variables: {
                                input: {
                                    elementId:
                                        selectedProjectElementNode?.elementId,
                                    projectId,
                                },
                                nodeId: selectedProjectElementNode?.id,
                            },
                        })
                    )
                }
                if (isDrawingTagUpdated) {
                    startTransition(() =>
                        getSelectedNuggetProductTypePropertiesView({
                            variables: {
                                input: {
                                    elementId:
                                        selectedProjectElementNode?.elementId,
                                    projectId,
                                },
                                nodeId: selectedProjectElementNode?.id,
                            },
                        })
                    )
                }
            }
        },
        [
            selectedProjectElementNode,
            projectId,
            getUpdatedProductListings,
            retrieveUpdatedPTPView,
            getSelectedNuggetProductTypePropertiesView,
            familySelected,
        ]
    )

    const [setProjectContentItemHidden] = useMutation<
        { setProjectContentItemIsHidden: Partial<DocumentTemplateView> },
        {
            SetProjectContentItemIsHiddenInput: Partial<
                SetProjectContentItemIsHiddenInput
            >
        }
    >(SetProjectContentItemIsHidden, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            if (isFamilyElement) {
                setWorkspaceDTV(data.setProjectContentItemIsHidden)
            } else if (isProductTypeElement) {
                if (projectNuggetId) {
                    const currentProductListings = (selectedProjectElementNode?.elementId &&
                        (projectProductTypeProductListingsInfo as Partial<
                            GetProjectNuggetResponse
                        >)?.getProjectNugget?.productListings?.filter(
                            (item) => {
                                return item.type?.includes('MANUFACTURER')
                            }
                        )) as Partial<ProjectManufacturerProductDetail>[]

                    setProjectProductTypeProductListingsInfo((past) => ({
                        getProjectNugget: {
                            ...(past as Partial<GetProjectNuggetResponse>)
                                ?.getProjectNugget,
                            productListings: currentProductListings?.map(
                                (item) => {
                                    return { ...item, isSelected: false }
                                }
                            ),
                            customManufacturerProducts: undefined,
                        },
                    }))
                }
                const incomingBODDtvItem = data.setProjectContentItemIsHidden?.items?.find(
                    (dtvItem) => dtvItem.isBOD
                ) as Partial<DocumentTemplateViewItem>
                const previousBODDtvItem = dtv?.items?.find(
                    (dtvItem) => dtvItem.isBOD
                ) as Partial<DocumentTemplateViewItem>
                const incomingManufacturersDtvItem = data.setProjectContentItemIsHidden?.items?.find(
                    (dtvItem) => dtvItem.isManufacturers
                ) as Partial<DocumentTemplateViewItem>
                const previousManufacturersDtvItem = dtv?.items?.find(
                    (dtvItem) => dtvItem.isManufacturers
                ) as Partial<DocumentTemplateViewItem>
                const isDTVHidden = (
                    dtvItem: Partial<DocumentTemplateViewItem>
                ) =>
                    dtvItem?.documentTemplateViewContents?.some((dtvCOntents) =>
                        dtvCOntents.documentTemplateViewContentItems?.some(
                            (contentItems) => contentItems.isHidden
                        )
                    )

                const isBODStatusChanged =
                    isDTVHidden(incomingBODDtvItem) !==
                    isDTVHidden(previousBODDtvItem)
                const isManufacturersStatusChanged =
                    isDTVHidden(incomingManufacturersDtvItem) !==
                    isDTVHidden(previousManufacturersDtvItem)

                if (isBODStatusChanged || isManufacturersStatusChanged) {
                    if (
                        expandedArticlesList.includes(
                            manufacturerArticleIds?.[0] as string
                        ) &&
                        selectedProjectElementNode?.elementId !== null
                    ) {
                        getSelectedNugget({
                            variables: {
                                elementId:
                                    selectedProjectElementNode?.elementId,
                                projectId,
                                nodeId: selectedProjectElementNode?.id,
                                productFamilyId: projectProductFamilyId,
                                partType: WorkspaceTabTitles[tabSelected ?? 0],
                                rootArticleContentItemIds: expandedArticlesList,
                            },
                        })

                        retrieveUpdatedNugget(true)
                    }
                }

                const mergedProductTypePropertiesView = getUpdatedItemsArray(
                    dtv?.items ?? [],
                    data.setProjectContentItemIsHidden.items ?? [],
                    false
                )
                setWorkspaceDTV({
                    ...dtv,
                    items: mergedProductTypePropertiesView,
                })
            }
            retrieveUpdatedElementChanges()
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_ProjectContentItemHidden',
                },
            })
        },
    })

    const [reorderFamilyTemplates] = useMutation<
        { reOrderFamilyContent: Partial<DocumentTemplateView> },
        { reOrderFamilyContentInput: Partial<ViewItemReorderInput> }
    >(ReorderFamilyContent, {
        fetchPolicy: 'no-cache',
        onCompleted: (data: {
            reOrderFamilyContent: Partial<DocumentTemplateView>
        }) => {
            setUpdatedDtv(data?.reOrderFamilyContent)
            setMoveUpAndDownTrigger('default')
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: 'WorspacePage_ReorderFamilyContentSuccess',
                },
            })
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_ReorderFamilyContent',
                },
            })
        },
    })

    const [reorderProductTypeTemplates] = useMutation<
        { reOrderPTContent: Partial<DocumentTemplateView> },
        { reOrderPTContentInput: Partial<ViewItemReorderPTPInput> }
    >(ReorderProductTypeContent, {
        fetchPolicy: 'no-cache',
        onCompleted: (data: {
            reOrderPTContent: Partial<DocumentTemplateView>
        }) => {
            startTransition(() =>
                handleDTVReorder(
                    tabSelected,
                    data.reOrderPTContent as DocumentTemplateView
                )
            )
            setMoveUpAndDownTrigger('default')
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: 'WorspacePage_ReorderFamilyContentSuccess',
                },
            })
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'WorspacePage_ReorderProductTypeContent',
                },
            })
        },
    })

    const handleInActivateProperty = React.useCallback(
        (
            setProjectContentItemIsHiddenInput: Partial<
                SetProjectContentItemIsHiddenInput
            >
        ) => {
            const setProjectContentItemIsHiddenInputs = {
                ...setProjectContentItemIsHiddenInput,
                isTrackChangeToggleOn: !!isTrackChangeToggleOn,
            }
            if (!!setProjectContentItemIsHiddenInput) {
                setProjectNuggetId(
                    setProjectContentItemIsHiddenInput.projectNuggetId
                )
                setProjectContentItemHidden({
                    variables: {
                        SetProjectContentItemIsHiddenInput: setProjectContentItemIsHiddenInputs,
                    },
                })
            }
        },
        [setProjectContentItemHidden, isTrackChangeToggleOn]
    )

    const retrieveUpdatedDtv = React.useCallback(
        (tab?: string) => {
            if (
                selectedProjectElementNode?.elementType ===
                ProjectElementType.PRODUCTFAMILY ||
                selectedTopic
            ) {
                startTransition(() =>
                    retrieveUpdatedFamilyDtv({
                        variables: {
                            input: {
                                elementId: selectedTopic
                                    ? selectedTopic?.ppfId
                                    : !isStudioManagement
                                        ? selectedProjectElementNode?.elementId
                                        : projectProductFamilyId,
                                projectId,
                            },
                            partType: selectedTopic
                                ? 'CONTENTGROUP'
                                : !isStudioManagement
                                    ? !!tab
                                        ? tab?.toUpperCase()
                                        : getTabName(tabSelected)?.toUpperCase()
                                    : undefined,
                            isTrackChangeToggleOn: isTrackChangeToggleOn,
                        },
                    })
                )
            } else if (selectedProjectElementNode?.elementId !== null) {
                startTransition(() =>
                    retrieveUpdatedPTPView({
                        variables: {
                            input: {
                                elementId:
                                    selectedProjectElementNode?.elementId,
                                projectId,
                            },
                            nodeId: selectedProjectElementNode?.id,
                            isTrackChangeToggleOn: isTrackChangeToggleOn,
                        },
                    })
                )
                startTransition(() =>
                    getProjectProductTypeProperties({
                        variables: {
                            input: {
                                elementId:
                                    selectedProjectElementNode?.elementId,
                                projectId,
                            },
                            nodeId: selectedProjectElementNode?.id,
                        },
                    })
                )
            }
        },
        [
            selectedProjectElementNode,
            getProjectProductTypeProperties,
            tabSelected,
            projectId,
            retrieveUpdatedPTPView,
            retrieveUpdatedFamilyDtv,
            isTrackChangeToggleOn,
            isStudioManagement,
            projectProductFamilyId,
            selectedTopic,
        ]
    )

    const retrieveUpdatedDtvPerPartType = React.useCallback(
        (tab?: string) => {
            if (
                selectedProjectElementNode?.elementType ===
                ProjectElementType.PRODUCTFAMILY
            ) {
                switch (tab) {
                    case 'GENERAL':
                        retrieveUpdatedFamilyDtvGeneral({
                            variables: {
                                input: {
                                    elementId: !isStudioManagement
                                        ? selectedProjectElementNode.elementId
                                        : projectProductFamilyId,
                                    projectId,
                                },
                                partType: tab.toUpperCase(),
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                        break
                    case 'PRODUCTS':
                        retrieveUpdatedFamilyDtvProducts({
                            variables: {
                                input: {
                                    elementId: !isStudioManagement
                                        ? selectedProjectElementNode.elementId
                                        : projectProductFamilyId,
                                    projectId,
                                },
                                partType: tab.toUpperCase(),
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                        break
                    case 'EXECUTION':
                        retrieveUpdatedFamilyDtvExecution({
                            variables: {
                                input: {
                                    elementId: !isStudioManagement
                                        ? selectedProjectElementNode.elementId
                                        : projectProductFamilyId,
                                    projectId,
                                },
                                partType: tab.toUpperCase(),
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                        break
                    case 'ALL':
                        // in case of SMM workspace part type is not needed
                        retrieveUpdatedFamilyDtvExecution({
                            variables: {
                                input: {
                                    elementId: !isStudioManagement
                                        ? selectedProjectElementNode.elementId
                                        : projectProductFamilyId,
                                    projectId,
                                },
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                        break
                    default:
                        break
                }
            }
        },
        [
            selectedProjectElementNode,
            getProjectProductTypeProperties,
            tabSelected,
            projectId,
            retrieveUpdatedPTPView,
            retrieveUpdatedFamilyDtvGeneral,
            retrieveUpdatedFamilyDtvProducts,
            retrieveUpdatedFamilyDtvExecution,
            isTrackChangeToggleOn,
            isStudioManagement,
            projectProductFamilyId,
        ]
    )

    const handleFamilyExpandClick = React.useCallback(
        (toExpand: boolean) => {
            startTransition(() =>
                setExpandedArticlesList(() => {
                    if (toExpand) {
                        const isLargeOMFamily =
                            isOMUser &&
                            allElementsList &&
                            allElementsList.length > OM_LARGE_FAMILY_THRESHOLD

                        if (isLargeOMFamily) {
                            return []
                        }

                        const allContentItemList = familySelected?.selected
                            ? projectProductFamilyData?.getProjectProductFamily
                                ?.contentItemNodes ?? []
                            : productFamilyData?.productFamily
                                ?.contentItemNodes ?? []
                        const contentItemList = isStudioManagement
                            ? allContentItemList?.filter(
                                (item) => !item?.isProductGroup
                            )
                            : allContentItemList
                        const newValue =
                            contentItemList.reduce(
                                (previous: string[], current) => {
                                    if (current.textType === 'Article') {
                                        if (
                                            !previous.includes(current.id ?? '')
                                        ) {
                                            return [
                                                ...previous,
                                                current.id ?? '',
                                            ]
                                        }
                                        return previous
                                    } else {
                                        return previous
                                    }
                                },
                                [] as string[]
                            ) ?? []

                        // Expand PT in Product group
                        const ptOnFamilyList =
                            projectProductFamilyData?.getProjectProductFamily
                                ?.familyAddedProductTypes
                        const dtvItems = dtv?.items?.filter(
                            (item: Partial<DocumentTemplateViewItem>) =>
                                !!item.isProductType
                        )
                        if (
                            !!newValue &&
                            !!ptOnFamilyList?.length &&
                            !!dtvItems?.length
                        ) {
                            ptOnFamilyList.map((item: ProjectElementNode) => {
                                const ptItem = dtvItems.find(
                                    (
                                        dtvItem: Partial<
                                            DocumentTemplateViewItem
                                        >
                                    ) => dtvItem.itemId === item.baseElementId
                                )
                                if (
                                    !!ptItem?.documentTemplateViewContents?.[0]
                                        ?.documentTemplateViewContentItems?.[0]
                                        ?.id
                                ) {
                                    newValue.push(
                                        ptItem
                                            ?.documentTemplateViewContents?.[0]
                                            ?.documentTemplateViewContentItems?.[0]
                                            ?.id
                                    )
                                }
                            })
                        }

                        return newValue
                    } else {
                        return []
                    }
                })
            )
        },
        [
            projectProductFamilyData,
            productFamilyData,
            familySelected,
            isStudioManagement,
            isOMUser,
            allElementsList,
        ]
    )

    const handleProductTypeExpandClick = React.useCallback(
        (toExpand: boolean) => {
            if (isStudioManagement && !!manufacturerArticleIds) {
                setExpandedArticlesList(manufacturerArticleIds!)
            } else {
                startTransition(() =>
                    setExpandedArticlesList(() => {
                        if (toExpand) {
                            const isLargeOMFamily =
                                isOMUser &&
                                allElementsList &&
                                allElementsList.length >
                                OM_LARGE_FAMILY_THRESHOLD

                            if (isLargeOMFamily) {
                                return []
                            }

                            const contentItemList = selectedProjectElementNode?.elementId
                                ? selectedNuggetData?.getProjectNugget
                                    ?.contentItemNodes ?? []
                                : nuggetData?.nugget?.contentItemNodes ?? []

                            return (
                                contentItemList.reduce(
                                    (previous: string[], current) => {
                                        if (current.textType === 'Article') {
                                            if (
                                                !previous.includes(
                                                    current.id ?? ''
                                                )
                                            ) {
                                                return [
                                                    ...previous,
                                                    current.id ?? '',
                                                ]
                                            }
                                            return previous
                                        } else {
                                            return previous
                                        }
                                    },
                                    [] as string[]
                                ) ?? []
                            )
                        } else {
                            return []
                        }
                    })
                )
            }
        },
        [
            selectedNuggetData,
            nuggetData,
            selectedProjectElementNode,
            isStudioManagement,
            manufacturerArticleIds,
            isOMUser,
            allElementsList,
        ]
    )

    const handleFamilyTabSelect = React.useCallback(
        (value: number) => {
            setWorkspaceTab(value)

            if (familySelected !== null) {
                if (value === 0 && !generalCalled) {
                    if (familySelected.selected) {
                        startTransition(() =>
                            getSelectedProductFamily({
                                variables: {
                                    elementId: familySelected.elementId,
                                    projectId,
                                    partType: 'GENERAL',
                                },
                            })
                        )

                        startTransition(() => {
                            getProjectProductFamilyDtv({
                                variables: {
                                    input: {
                                        elementId: familySelected.elementId,
                                        projectId,
                                    },
                                    partType: 'GENERAL',
                                    isTrackChangeToggleOn: isTrackChangeToggleOn,
                                },
                            })
                        })
                    } else {
                        startTransition(() =>
                            getProductFamily({
                                variables: {
                                    id: familySelected.elementId,
                                    projectId,
                                    partType: 'GENERAL',
                                },
                            })
                        )
                        startTransition(() =>
                            getProductFamilyDtv({
                                variables: {
                                    id: familySelected.elementId,
                                    partType: 'GENERAL',
                                },
                            })
                        )
                    }
                } else if (value === 1 && !productsCalled) {
                    if (familySelected.selected) {
                        startTransition(() =>
                            getSelectedProductFamily({
                                variables: {
                                    elementId: familySelected.elementId,
                                    projectId,
                                    partType: 'PRODUCTS',
                                    familyNodeId: familySelected?.familyNodeId,
                                },
                            })
                        )

                        startTransition(() =>
                            getProjectProductFamilyDtv({
                                variables: {
                                    input: {
                                        elementId: familySelected.elementId,
                                        projectId,
                                    },
                                    partType: 'PRODUCTS',
                                    isTrackChangeToggleOn: isTrackChangeToggleOn,
                                },
                            })
                        )
                    } else {
                        startTransition(() =>
                            getProductFamily({
                                variables: {
                                    id: familySelected.elementId,
                                    projectId,
                                    partType: 'PRODUCTS',
                                },
                            })
                        )
                        startTransition(() =>
                            getProductFamilyDtv({
                                variables: {
                                    id: familySelected.elementId,
                                    partType: 'PRODUCTS',
                                },
                            })
                        )
                    }
                } else if (value === 2 && !executionCalled) {
                    if (familySelected.selected) {
                        startTransition(() =>
                            getSelectedProductFamily({
                                variables: {
                                    elementId: familySelected.elementId,
                                    projectId,
                                    partType: 'EXECUTION',
                                },
                            })
                        )

                        startTransition(() =>
                            getProjectProductFamilyDtv({
                                variables: {
                                    input: {
                                        elementId: familySelected.elementId,
                                        projectId,
                                    },
                                    partType: 'EXECUTION',
                                    isTrackChangeToggleOn: isTrackChangeToggleOn,
                                },
                            })
                        )
                    } else {
                        startTransition(() =>
                            getProductFamily({
                                variables: {
                                    id: familySelected.elementId,
                                    projectId,
                                    partType: 'EXECUTION',
                                },
                            })
                        )
                        startTransition(() =>
                            getProductFamilyDtv({
                                variables: {
                                    id: familySelected.elementId,
                                    partType: 'EXECUTION',
                                },
                            })
                        )
                    }
                }
            }
            setWorkspaceTemplateData(undefined)
            setPromoteDemoteWorkspaceTemplateData(undefined)
            if (isAddProhibited) {
                setIsAddProhibited(false)
            }
            if (isAddOngoing) {
                setIsAddOngoing(false)
            }
        },
        [
            familySelected,
            getProductFamily,
            getSelectedProductFamily,
            getProductFamilyDtv,
            getProjectProductFamilyDtv,
            projectId,
            generalCalled,
            productsCalled,
            executionCalled,
            isAddProhibited,
            isAddOngoing,
            isTrackChangeToggleOn,
        ]
    )

    const handleProductTypeTabSelect = React.useCallback(
        (value: number) => {
            setWorkspaceTab(value)
            if (isAddProhibited) {
                setIsAddProhibited(false)
            }
            if (isAddOngoing) {
                setIsAddOngoing(false)
            }
            setIsNuggetRetrieved(false)
        },
        [isAddProhibited, isAddOngoing]
    )

    const handleFamilyReorderTemplates = React.useCallback(
        (reOrderFamilyContentInput: Partial<ViewItemReorderInput>) => {
            reorderFamilyTemplates({
                variables: {
                    reOrderFamilyContentInput,
                },
            })
        },
        [reorderFamilyTemplates]
    )

    const handleProductTypeReorderTemplates = React.useCallback(
        (reOrderPTContentInput: Partial<ViewItemReorderPTPInput>) => {
            startTransition(() =>
                reorderProductTypeTemplates({
                    variables: {
                        reOrderPTContentInput,
                    },
                })
            )
        },
        [reorderProductTypeTemplates]
    )

    const updateFamilyArticleData = React.useCallback(
        (
            newData:
                | Partial<GetProductFamilyResponse>
                | Partial<GetProjectProductFamilyResponse>,
            selected?: boolean,
            overwrite?: boolean
        ) => {
            if (selected) {
                setProjectProductFamilyData((past) => {
                    const updatedValue = {
                        ...newData,
                        getProjectProductFamily: {
                            ...(newData as GetProjectProductFamilyResponse)
                                .getProjectProductFamily,
                            contentItemNodes: getUpdatedItemsArray(
                                contentItemNodes as Partial<ContentItem>[],
                                (newData as GetProjectProductFamilyResponse)
                                    .getProjectProductFamily
                                    .contentItemNodes as Partial<ContentItem>[],
                                true,
                                overwrite
                            ),
                        },
                    } as GetProjectProductFamilyResponse
                    setWorkspaceContentItemsViaNugget(undefined, updatedValue)
                    return updatedValue
                })
                setWorkspacePTPs(
                    undefined,
                    (newData as any)?.getProjectProductFamily
                        ?.projectProductTypeProperties,
                    ProjectElementType.PRODUCTFAMILY
                )
            } else {
                setProductFamilyData((past) => {
                    const newValue = mergeProductFamilyContentItemNodes(
                        past,
                        newData as GetProductFamilyResponse,
                        contentIdsToReplaceOnNextFetch
                    )
                    setWorkspaceContentItemsViaNugget(undefined, newValue)
                    return newValue
                })
            }
        },
        [productFamilyData, contentIdsToReplaceOnNextFetch]
    )
    const retrieveUpdatedElementChanges = React.useCallback(() => {
        if (
            selectedProjectElementNode &&
            (selectedTopic?.ppfId || selectedProjectElementNode?.elementId)
        ) {
            const apiMethod =
                selectedProjectElementNode?.elementType === 'ASSEMBLY' &&
                    !selectedTopic
                    ? getAssemblyChanges
                    : selectedProjectElementNode?.elementType ===
                        'PRODUCTFAMILY' || selectedTopic
                        ? getFamilyChanges
                        : getPTTrackedChanges

            startTransition(() =>
                apiMethod({
                    variables: {
                        input: {
                            projectId,
                            elementId: selectedTopic
                                ? selectedTopic?.ppfId
                                : selectedProjectElementNode?.elementId,
                        },
                        includeProductTypeInFamilyTrackChanges:
                            selectedProjectElementNode?.elementType ===
                            ProjectElementType.PRODUCTFAMILY,
                    },
                })
            )
        }
    }, [
        selectedProjectElementNode,
        projectId,
        getAssemblyChanges,
        getFamilyChanges,
        selectedTopic,
        getPTTrackedChanges,
    ])
    const handleContentItemUpdate = React.useCallback(
        ({
            updatedContentItems,
            projectNuggets,
            retrieveTrackedChanges,
            overwriteContentItems = false,
            contentItemNodesInner,
            updatedDtv,
            mergeDtv = false,
            newIsAddOngoing,
            newIsAddProhibited,
        }: HandleContentItemUpdateParams) => {
            if (!updatedContentItems) return

            let updatedItemsArray: PartialItems[] = []
            if (isProductTypeElement) {
                updatedItemsArray = getUpdatedItemsArray(
                    contentItemNodes as Partial<ContentItem>[],
                    updatedContentItems,
                    true,
                    overwriteContentItems
                )
            } else {
                updatedItemsArray = getUpdatedItemsArray(
                    (contentItemNodesInner ?? contentItemNodes) as Partial<
                        ContentItem
                    >[],
                    updatedContentItems,
                    true,
                    overwriteContentItems
                )
                setProjectProductFamilyData((past) => {
                    const nuggets = mergeProjectNuggets(
                        projectProductFamilyData?.getProjectProductFamily
                            ?.projectNuggets ?? [],
                        projectNuggets ?? []
                    )

                    const newValue = {
                        ...past,
                        getProjectProductFamily: {
                            ...past?.getProjectProductFamily,
                            projectNuggets: nuggets,
                        },
                    }
                    return newValue
                })
            }

            // Updat reactive variable fields in one go to avoid extra re-renders.
            startTransition(() =>
                updateWorkspaceContents({
                    contentItems: updatedItemsArray,
                    updatedDtv: updatedDtv,
                    mergeDtv: mergeDtv,
                    isAddOngoing: newIsAddOngoing,
                    isAddProhibited: newIsAddProhibited,
                })
            )

            if (retrieveTrackedChanges) {
                retrieveUpdatedElementChanges()
            }
        },
        [
            isProductTypeElement,
            contentItemNodes,
            projectProductFamilyData,
            retrieveUpdatedElementChanges,
        ]
    )

    const processUpdatedFamilyContent = React.useCallback(
        (updatedProjectContent: Partial<UpdateProjectContent>) => {
            const newItems = getNewlyAddedItems(
                updatedProjectContent?.contentItemNodes as Partial<
                    ContentItem
                >[],
                contentItemNodes as Partial<ContentItem>[]
            )
            setAddQueueSib(newItems[0].id as string)
            startTransition(() =>
                handleContentItemUpdate({
                    updatedContentItems: newItems ?? [],
                    projectNuggets: updatedProjectContent?.projectNuggets,
                    updatedDtv: updatedProjectContent?.documentTemplateView,
                    mergeDtv: true,
                })
            )
        },
        [handleContentItemUpdate]
    )

    const processUpdatedProductTypeContent = React.useCallback(
        (updatedProjectContent: Partial<UpdateProjectContent>) => {
            const newItems = getNewlyAddedItems(
                updatedProjectContent?.contentItemNodes as Partial<
                    ContentItem
                >[],
                contentItemNodes as Partial<ContentItem>[]
            )
            setAddQueueSib(newItems[0].id as string)
            startTransition(() =>
                handleContentItemUpdate({
                    updatedContentItems: newItems ?? [],
                    projectNuggets: updatedProjectContent.projectNuggets,
                    updatedDtv:
                        updatedProjectContent?.projectNuggets?.[0]
                            ?.projectProductTypePropertiesView,
                })
            )
            // update Project Nugget Product Type Properties with
            // newly-added item
            setProjectProductTypeProductListingsInfo((past) => {
                const pastValue = { ...past } as Partial<
                    GetProjectNuggetResponse
                >
                const updatedProjectNuggets = mergeProjectNuggets(
                    [pastValue?.getProjectNugget ?? {}],
                    updatedProjectContent.projectNuggets ?? []
                )[0]

                setWorkspacePTPs(
                    [updatedProjectNuggets!] as Partial<ProjectNugget>[],
                    updatedProjectNuggets.projectProductTypeProperties,
                    ProjectElementType.PRODUCTTYPE
                )
                const result = {
                    ...pastValue,
                    getProjectNugget: {
                        ...updatedProjectNuggets,
                    },
                } as typeof past

                return result
            })
        },
        [handleContentItemUpdate, setProjectProductTypeProductListingsInfo]
    )

    const handleBestPracticeProperty = React.useCallback(
        (item: Partial<ContentItem>) => {
            startTransition(() =>
                handleContentItemUpdate({ updatedContentItems: [item] })
            )
        },
        [handleContentItemUpdate]
    )

    const handleHtmlParagraphPaste = React.useCallback(
        ({
            rootArticleContentItemIds,
            ...rest
        }: ExtendedContentInsertVariable) => {
            setContentInsertVariables({
                input: {
                    ...rest,
                    projectId,
                    productTypeNuggetId: productTypeSelected,
                },
                partType: rest.partType,
                productFamilyId: rest.productFamilyId,
                rootArticleContentItemIds: rootArticleContentItemIds ?? [],
            })
            setIsPasteInProgress(true)
        },
        [isPasteInProgress, projectId, productTypeSelected]
    )

    const updateProductArticleData = React.useCallback(
        (
            newData: GetNuggetResponse | GetProjectNuggetResponse,
            selected?: boolean,
            overwrite?: boolean,
            isItemRemoved?: boolean
        ) => {
            if (!selected) {
                setNuggetData((past) => {
                    const data = newData as GetNuggetResponse
                    const res = {
                        ...past,
                        nugget: {
                            ...past?.nugget,
                            contentItemNodes: overwrite
                                ? [...(data?.nugget?.contentItemNodes ?? [])]
                                : [
                                    ...(past?.nugget?.contentItemNodes ?? []),
                                    ...(data?.nugget?.contentItemNodes ?? []),
                                ],
                        },
                    }
                    setWorkspaceContentItemsViaNugget(res)
                    return res as GetNuggetResponse
                })
            } else {
                const data = newData as GetProjectNuggetResponse
                startTransition(() =>
                    handleContentItemUpdate({
                        updatedContentItems:
                            data?.getProjectNugget?.contentItemNodes ?? [],
                        overwriteContentItems: overwrite,
                    })
                )
            }
        },
        [handleContentItemUpdate]
    )

    const resetTabVisitStates = React.useCallback(() => {
        for (let tabIndex = 0; tabIndex < 3; tabIndex++) {
            updateTabVisitStates(tabIndex, false)
        }
    }, [updateTabVisitStates])

    React.useEffect(() => {
        if (resetOtherTabs) {
            resetTabVisitStates()
        }
    }, [resetTabVisitStates, resetOtherTabs])

    // This prevents the loading spinner from getting stuck when navigating from New to Old workspace
    React.useEffect(() => {
        // Check if workspace toggle has changed
        if (
            previousIsBetaWorkspaceToggleEnabled !== undefined &&
            previousIsBetaWorkspaceToggleEnabled !==
            isBetaWorkspaceToggleEnabled
        ) {
            // Reset all family-related states to prevent stale data
            setFamilySelected(null)
            setLoadingBackdrop(false)
            setProductFamilyData(undefined)
            setProjectProductFamilyData(undefined)
            setWorkspaceContentItems([])
            setWorkspaceDTV(null)
            setExpandedArticlesList([])

            // Reset tab states
            setGeneralCalled(false)
            setProductsCalled(false)
            setExecutionCalled(false)
            setWorkspaceTab(0)
            setFamilyItemsExpanded(false)
            setProductTypeItemsExpanded(false)

            // Reset tab visit states
            resetTabVisitStates()

            // Clear any workspace errors
            setWorkspaceHasError(false)

            // Reset nugget data if needed
            setNuggetData(undefined)
            setSelectedNuggetData(undefined)
            setIsNuggetRetrieved(false)
        }
    }, [
        isBetaWorkspaceToggleEnabled,
        previousIsBetaWorkspaceToggleEnabled,
        resetTabVisitStates,
    ])

    React.useEffect(() => {
        const isContentAreaBackdropOpen =
            loadingBackdrop ||
            (productFamilyData === null && projectProductFamilyData === null) ||
            (nuggetData === null && selectedNuggetData === null) ||
            addNewPT
        setContentAreaLoading(isContentAreaBackdropOpen)
    }, [
        loadingBackdrop,
        productFamilyData,
        projectProductFamilyData,
        nuggetData,
        selectedNuggetData,
        addNewPT,
    ])

    // side-effect to set the element properties and notes visibility initial value from storage to respective states
    React.useEffect(() => {
        setIsElementNoteVisible(initialNotesVisibleValue)
        setIsElementPropertiesVisible(initialPropertyVisibleValue)
    }, [initialNotesVisibleValue, initialPropertyVisibleValue])

    // side-effect to reset the workspace actions (Action bar buttons) on initial loading of content area
    React.useEffect(() => {
        // to limit the number of resets (only when some button are enabled or requirements are empty)
        if (isWorkspaceActionsNotDefault) {
            setReactiveWorkspaceActions('default')
        }
    }, [isWorkspaceActionsNotDefault])

    React.useEffect(() => {
        const previousElement = (previousSelectedElement as unknown) as ProjectElementNode
        setExpandedArticlesList((past) =>
            selectedProjectElementNode?.treePath ===
                previousElement?.treePath ||
                selectedProjectElementNode?.id === previousElement?.id
                ? past
                : []
        )

        //every change of element node loading must be displayed
        const selectedElementChanged =
            previousElement &&
            selectedProjectElementNode &&
            selectedProjectElementNode?.treePath !==
            previousElement?.treePath &&
            previousElement?.baseElementId !==
            selectedProjectElementNode?.baseElementId
        if (selectedElementChanged) {
            if (
                selectedProjectElementNode?.elementType === 'PRODUCTTYPE' &&
                contentTeamFromStudioPage
            ) {
                setLoadingBackdrop(false)
            } else {
                setLoadingBackdrop(true)
            }
            setWorkspaceDTV(null)
            setWorkspaceContentItems([])
        }
        if (
            selectedProjectElementNode?.elementType === 'ASSEMBLY' &&
            !selectedTopic
        ) {
            setLoadingBackdrop(isDataLoading)
        }
        if (isBulkAddDataLoading) {
            // always show spinner if we're waiting for child items to load via shift+click.
            setLoadingBackdrop(true)
        } else if (previousIsBulkAddDataLoading && !isBulkAddDataLoading) {
            // clear spinner after shift+click load completes
            setLoadingBackdrop(false)
        }
        if (workspaceError) {
            setWorkspaceHasError(false)
        }

        setStateValue('retrieveUpdatedNugget', retrieveUpdatedNugget)
    }, [
        selectedProjectElementNode,
        isFamilyElement,
        previousSelectedElement,
        isDataLoading,
        workspaceError,
        familySelected,
        selectedTopic,
        isBulkAddDataLoading,
        previousIsBulkAddDataLoading,
    ])

    React.useEffect(() => {
        /**
         * Load when the family is pressed
         * Load when the family is selected
         */
        if (
            selectedProjectElementNode?.elementType === 'PRODUCTFAMILY' &&
            ((selectedProjectElementNode.elementId &&
                familySelected?.elementId !==
                selectedProjectElementNode.elementId) ||
                (!selectedProjectElementNode.elementId &&
                    familySelected?.elementId !==
                    selectedProjectElementNode?.baseElementId) ||
                (!!selectedProjectElementNode.elementId &&
                    !!selectedProjectElementNode?.treePath &&
                    familySelected?.treePath !==
                    selectedProjectElementNode.treePath))
        ) {
            if (selectedProjectElementNode?.elementId) {
                setProjectProductFamilyData(undefined)
                setWorkspaceContentItems([])
                setFamilySelected({
                    elementId: selectedProjectElementNode?.elementId,
                    selected: true,
                    projectId: selectedProjectElementNode?.projectId,
                    familyNodeId: selectedProjectElementNode?.id,
                    treePath: selectedProjectElementNode?.treePath,
                })
                if (isAddOngoing) {
                    setIsAddOngoing(false)
                }

                if (isAddProhibited) {
                    setIsAddProhibited(false)
                }
            } else {
                setProductFamilyData(undefined)
                setWorkspaceContentItems([])
                setFamilySelected({
                    elementId: selectedProjectElementNode?.baseElementId,
                    selected: false,
                })
            }
            resetTabVisitStates()
            setWorkspaceTab(0)
            setFamilyItemsExpanded(false)
        }

        if (
            selectedProjectElementNode?.elementType !== 'PRODUCTFAMILY' &&
            selectedTopic === undefined
        ) {
            setFamilySelected(null)
        } else {
            setProductTypeSelected(null)
            setIsNuggetRetrieved(false)
        }

        if (isProductTypeElement) {
            setFamilyAddedProductTypeData()
            // Determine whether product type element is added to project or not
            if (selectedProjectElementNode?.elementId !== null) {
                if (
                    !dtv ||
                    (dtv &&
                        Object.keys(dtv).length === 0 &&
                        selectedProjectElementNode?.id)
                ) {
                    startTransition(() =>
                        getSelectedNuggetProductTypePropertiesView({
                            variables: {
                                input: {
                                    elementId:
                                        selectedProjectElementNode?.elementId,
                                    projectId,
                                },
                                nodeId: selectedProjectElementNode?.id,
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                    )
                    startTransition(() =>
                        getProjectProductTypeProductListingsInfo({
                            variables: {
                                input: {
                                    elementId:
                                        selectedProjectElementNode?.elementId,
                                    projectId,
                                },
                                nodeId: selectedProjectElementNode?.id,
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                    )
                } else {
                    if (
                        (tabSelected === 0 && !generalCalled) ||
                        (tabSelected === 1 && !productsCalled) ||
                        (tabSelected === 2 && !executionCalled)
                    ) {
                        if (
                            selectedProjectElementNode?.elementId ===
                            selectedNuggetData?.getProjectNugget?.id &&
                            !isNuggetRetrieved
                        ) {
                            startTransition(() =>
                                getSelectedNugget({
                                    variables: {
                                        elementId:
                                            selectedProjectElementNode?.elementId,
                                        projectId,
                                        nodeId: selectedProjectElementNode?.id,
                                        productFamilyId: projectProductFamilyId,
                                        partType:
                                            WorkspaceTabTitles[
                                            tabSelected ?? 0
                                            ],
                                    },
                                })
                            )

                            if (isAddOngoing) {
                                setIsAddOngoing(false)
                            }

                            if (isAddProhibited) {
                                setIsAddProhibited(false)
                            }
                        }
                        startTransition(() =>
                            getProjectProductTypeProductListingsInfo({
                                variables: {
                                    input: {
                                        elementId:
                                            selectedProjectElementNode?.elementId,
                                        projectId,
                                    },
                                    nodeId: selectedProjectElementNode?.id,
                                    isTrackChangeToggleOn: isTrackChangeToggleOn,
                                },
                            })
                        )
                    }
                }
            } else {
                if (!dtv || (dtv && Object.keys(dtv).length === 0)) {
                    startTransition(() =>
                        getNuggetProductTypePropertiesView({
                            variables: {
                                id: selectedProjectElementNode?.baseElementId,
                                nodeId: selectedProjectElementNode?.id,
                                isTrackChangeToggleOn: isTrackChangeToggleOn,
                            },
                        })
                    )
                } else {
                    if (
                        (tabSelected === 0 && !generalCalled) ||
                        (tabSelected === 1 && !productsCalled) ||
                        (tabSelected === 2 && !executionCalled)
                    ) {
                        if (
                            selectedProjectElementNode?.baseElementId ===
                            nuggetData?.nugget?.id &&
                            projectProductFamilyId
                        ) {
                            startTransition(() =>
                                getNugget({
                                    variables: {
                                        id:
                                            selectedProjectElementNode?.baseElementId,
                                        projectId,
                                        productFamilyId: projectProductFamilyId,
                                        partType:
                                            WorkspaceTabTitles[
                                            tabSelected ?? 0
                                            ],
                                        nodeId: selectedProjectElementNode?.id,
                                        rootArticleContentItemIds: expandedArticlesList,
                                    },
                                })
                            )

                            startTransition(() =>
                                getProductTypeProductListingsInfo({
                                    variables: {
                                        id:
                                            selectedProjectElementNode?.baseElementId,
                                        nodeId: selectedProjectElementNode?.id,
                                    },
                                })
                            )
                        }
                    }
                }
            }
            if (
                (selectedProjectElementNode?.elementId &&
                    selectedProjectElementNode?.elementId !==
                    productTypeSelected) ||
                (!selectedProjectElementNode?.elementId &&
                    selectedProjectElementNode?.baseElementId !==
                    productTypeSelected)
            ) {
                setProductTypeSelected(
                    (selectedProjectElementNode?.elementId ??
                        selectedProjectElementNode?.baseElementId) as string
                )
                setWorkspaceDTV(null)
                setWorkspaceTab(1)
                setProductTypeItemsExpanded(false)
                resetTabVisitStates()
            }
            setWorkspaceTemplateData(undefined)
            setPromoteDemoteWorkspaceTemplateData(undefined)
        }
    }, [
        selectedProjectElementNode,
        familySelected,
        getSelectedNugget,
        getNuggetProductTypePropertiesView,
        projectId,
        productTypeSelected,
        tabSelected,
        resetTabVisitStates,
        executionCalled,
        generalCalled,
        productsCalled,
        isProductTypeElement,
        getNugget,
        getSelectedNuggetProductTypePropertiesView,
        dtv,
        projectProductFamilyData,
        productFamilyData,
        selectedNuggetData,
        isNuggetRetrieved,
        expandedArticlesList,
        getProductTypeProductListingsInfo,
        getProjectProductTypeProductListingsInfo,
        nuggetData,
        projectProductFamilyId,
        isAddOngoing,
        isAddProhibited,
        selectedTopic,
        isTrackChangeToggleOn,
        isRemoveNuggetGqlOngoing,
    ])

    React.useEffect(() => {
        if (isStudioManagement && selectedTopic) {
            if (!isBetaWorkspaceToggleEnabled) {
                startTransition(() =>
                    getSelectedProductFamilyWithTable({
                        variables: {
                            elementId: selectedTopic?.ppfId,
                            familyNodeId: selectedTopic?.projectElementNodeId,
                            projectId,
                            partType: 'CONTENTGROUP',
                        },
                    })
                )
            }
            startTransition(() => {
                getProjectProductFamilyDtv({
                    variables: {
                        input: {
                            elementId: selectedTopic?.ppfId,
                            projectId,
                        },
                        partType: 'CONTENTGROUP',
                        isTrackChangeToggleOn: isTrackChangeToggleOn,
                    },
                })
            })
        } else if (
            familySelected !== null &&
            tabSelected === 0 &&
            !generalCalled
        ) {
            if (isAddRemoveClicked) {
                setLoadingBackdrop(false)
            }

            if (familySelected.selected) {
                startTransition(() =>
                    getSelectedProductFamilyProjectElementMetadata({
                        variables: {
                            input: {
                                elementId: familySelected.elementId,
                                projectId,
                            },
                        },
                    })
                )
                if (!isBetaWorkspaceToggleEnabled) {
                    startTransition(() =>
                        getSelectedProductFamilyWithTable({
                            variables: {
                                ...familySelected,
                                elementId: familySelected.elementId,
                                projectId,
                                partType: isStudioManagement
                                    ? undefined
                                    : 'GENERAL',
                            },
                        })
                    )
                }

                startTransition(() =>
                    getSelectedProjectProductFamilyDtv({
                        variables: {
                            input: {
                                elementId: familySelected.elementId,
                                projectId,
                            },
                            partType: isStudioManagement
                                ? undefined
                                : 'GENERAL',
                            isTrackChangeToggleOn,
                        },
                    })
                )
            } else {
                startTransition(() =>
                    getProductFamily({
                        variables: {
                            id: familySelected.elementId,
                            projectId,
                            partType: 'GENERAL',
                        },
                    })
                )
                startTransition(() =>
                    getProductFamilyDtv({
                        variables: {
                            id: familySelected.elementId,
                            partType: 'GENERAL',
                        },
                    })
                )
            }
        }
    }, [
        isAddRemoveClicked,
        tabSelected,
        projectId,
        familySelected,
        getSelectedProductFamily,
        getProductFamily,
        generalCalled,
        getProductFamilyDtv,
        getSelectedProjectProductFamilyDtv,
        getSelectedProductFamilyProjectElementMetadata,
        isStudioManagement,
        selectedTopic,
        isTrackChangeToggleOn,
        isStudioManagement,
    ])

    const handleHtmlParagraphPasteCompleted = React.useCallback(
        (result: Partial<UpdateProjectContent>) => {
            if (
                selectedProjectElementNode?.elementType ===
                ProjectElementType.PRODUCTTYPE
            ) {
                processUpdatedProductTypeContent(result)
            } else {
                processUpdatedFamilyContent(result)
            }
            showGlobalSnackbar({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `WorkspacePage_PasteContentItemsSuccess`,
                },
            })
            retrieveUpdatedElementChanges()
            setIsPasteInProgress(false)
        },
        [
            isPasteInProgress,
            processUpdatedProductTypeContent,
            processUpdatedFamilyContent,
        ]
    )
    const elementToggleHandler = React.useCallback(
        (toggleType: ToggleHandlerType, value: boolean = true) => {
            const toggleActions: Partial<Record<
                ToggleHandlerType,
                (val: boolean) => void
            >> = {
                notes: setIsElementNoteVisible,
                property: setIsElementPropertiesVisible,
            }

            toggleActions[toggleType]?.(value)
        },
        []
    )

    const handleHtmlParagraphPasteError = React.useCallback(
        (errorMessage: string | null | undefined) => {
            showGlobalSnackbar({
                variables: {
                    message:
                        errorMessage ??
                        'An unspecified error was encountered while pasting your content.',
                    variant: 'error',
                    id: `WorkspacePage_HtmlParagraphPasteError`,
                },
            })
            setIsPasteInProgress(false)
        },
        [showGlobalSnackbar, isPasteInProgress]
    )

    const handleHtmlParagraphPasteCancelled = React.useCallback(() => {
        setIsPasteInProgress(false)
    }, [isPasteInProgress])

    const classes = useWorkspaceContentAreaStyles({
        elementType: selectedProjectElementNode?.elementType,
        isContentGroup:
            contentTeamFromStudioPage && selectedTopic !== undefined,
        isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
    })

    // Read data from service worker
    React.useEffect(() => {
        if (!serviceWorker) return

        const handleServiceWorkerMessage = ({ data }: MessageEvent) => {
            const workspaceManufacturerSelectedState = data[
                WORKSPACE_MANUFACTURER_SELECTED_KEY
            ] as WorkspaceManufacturerSelected

            if (
                workspaceManufacturerSelectedState !== undefined &&
                workspaceManufacturerSelectedState.listingId !== undefined &&
                isProductTypeElement
            ) {
                if (workspaceManufacturerSelectedState?.isFirstBOD) {
                    setProjectProductTypeProductListingsInfo((past) => ({
                        getProjectNugget: {
                            ...(past as Partial<GetProjectNuggetResponse>)
                                ?.getProjectNugget,
                            productListings: (past as Partial<
                                GetProjectNuggetResponse
                            >)?.getProjectNugget?.productListings?.map(
                                (item) => {
                                    return { ...item, isSelected: false }
                                }
                            ),
                            customManufacturerProducts: undefined,
                        },
                    }))
                }
                if (!workspaceManufacturerSelectedState.isBOD) {
                    const currentProductListings = (selectedProjectElementNode?.elementId &&
                        (projectProductTypeProductListingsInfo as Partial<
                            GetProjectNuggetResponse
                        >)?.getProjectNugget?.productListings) as Partial<
                            ProjectManufacturerProductDetail
                        >[]

                    if (currentProductListings) {
                        let updatedListings: Partial<
                            ProjectManufacturerProductDetail
                        >[] = []
                        const manufacturer = currentProductListings?.find(
                            (listing) =>
                                listing.listingId ===
                                workspaceManufacturerSelectedState.listingId
                        )
                        if (
                            manufacturer?.type?.toLowerCase() !==
                            ListingLevel.Manufacturer.toLowerCase() &&
                            !workspaceManufacturerSelectedState.isSelected &&
                            currentProductListings
                        ) {
                            updatedListings = currentProductListings
                                ?.filter(
                                    (listing) =>
                                        listing.listingId !==
                                        manufacturer?.listingId
                                )
                                .sort((firstElement, secondElement) =>
                                    (firstElement.listingText as string) >
                                        (secondElement.listingText as string)
                                        ? 1
                                        : (secondElement.listingText as string) <
                                            (firstElement.listingText as string)
                                            ? -1
                                            : 0
                                )
                        } else {
                            if (!manufacturer) {
                                updatedListings = (currentProductListings as Partial<
                                    ProjectManufacturerProductDetail
                                >[])
                                    ?.concat({
                                        listingText:
                                            workspaceManufacturerSelectedState?.listingText,
                                        listingId:
                                            workspaceManufacturerSelectedState?.listingId,
                                        type:
                                            workspaceManufacturerSelectedState?.listingType,
                                        firmName:
                                            workspaceManufacturerSelectedState?.firmName,
                                        isBOD:
                                            workspaceManufacturerSelectedState.isBOD,
                                        isSelected:
                                            workspaceManufacturerSelectedState.isSelected,
                                    })
                                    .sort((firstElement, secondElement) =>
                                        (firstElement.listingText as string) >
                                            (secondElement.listingText as string)
                                            ? 1
                                            : (secondElement.listingText as string) <
                                                (firstElement.listingText as string)
                                                ? -1
                                                : 0
                                    )
                            } else {
                                updatedListings = (currentProductListings as Partial<
                                    ProjectManufacturerProductDetail
                                >[])
                                    ?.map((each) =>
                                        each.listingId ===
                                            workspaceManufacturerSelectedState.listingId
                                            ? {
                                                ...each,
                                                isSelected:
                                                    workspaceManufacturerSelectedState.isSelected,
                                            }
                                            : each
                                    )
                                    .sort((firstElement, secondElement) =>
                                        (firstElement.listingText as string) >
                                            (secondElement.listingText as string)
                                            ? 1
                                            : (secondElement.listingText as string) <
                                                (firstElement.listingText as string)
                                                ? -1
                                                : 0
                                    )
                            }
                        }

                        setProjectProductTypeProductListingsInfo((past) => ({
                            getProjectNugget: {
                                ...(past as Partial<GetProjectNuggetResponse>)
                                    ?.getProjectNugget,
                                productListings: updatedListings,
                            },
                        }))
                    }
                } else {
                    if (!workspaceManufacturerSelectedState.isSelected) {
                        const currentProductListings = (selectedProjectElementNode?.elementId &&
                            (projectProductTypeProductListingsInfo as Partial<
                                GetProjectNuggetResponse
                            >)?.getProjectNugget?.productListings) as Partial<
                                ProjectManufacturerProductDetail
                            >[]

                        if (currentProductListings) {
                            const updatedListings = currentProductListings
                                .filter(
                                    (listing) =>
                                        listing.listingId !==
                                        workspaceManufacturerSelectedState.listingId
                                )
                                .sort((firstElement, secondElement) =>
                                    (firstElement.listingText as string) >
                                        (secondElement.listingText as string)
                                        ? 1
                                        : (secondElement.listingText as string) <
                                            (firstElement.listingText as string)
                                            ? -1
                                            : 0
                                )

                            setProjectProductTypeProductListingsInfo((past) => ({
                                getProjectNugget: {
                                    ...(past as Partial<GetProjectNuggetResponse>)
                                        ?.getProjectNugget,
                                    productListings: updatedListings,
                                },
                            }))
                        }
                    }

                    const bodDTV = dtv?.items?.find((dtvItem) => dtvItem?.isBOD)

                    if (
                        bodDTV?.documentTemplateViewContents?.some(
                            (dtvContents) =>
                                dtvContents?.documentTemplateViewContentItems?.some(
                                    (contentItems) =>
                                        contentItems?.isHidden ||
                                        (!contentItems?.isHidden &&
                                            contentItems?.order === 0)
                                )
                        ) &&
                        selectedProjectElementNode?.isInProject
                    ) {
                        updateContentItemHiddenStatus(
                            bodDTV,
                            selectedProjectElementNode,
                            familyAddedProductTypes,
                            projectId as string,
                            handleInActivateProperty,
                            undefined,
                            undefined,
                            projectProductTypeProductListingsInfo,
                            dtv,
                            true
                        )
                    }
                }
            }

            if (data) {
                processManufacturerServiceWorkerData(
                    data,
                    isProductTypeElement,
                    retrieveUpdatedNugget,
                    handleContentItemUpdate,
                    expandedArticlesList,
                    manufacturerArticleIds
                )

                const workspaceNoteCountState = data[
                    WORKSPACE_NOTE_COUNT_UPDATE
                ] as WorkspaceNotesCountUpdate

                if (
                    workspaceNoteCountState &&
                    workspaceNoteCountState?.action
                ) {
                    updateElementNotesCount(workspaceNoteCountState.action)
                }
            }
        }

        serviceWorker?.addEventListener('message', handleServiceWorkerMessage)
        return () => {
            serviceWorker?.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
        }
    }, [
        serviceWorker,
        handleContentItemUpdate,
        retrieveUpdatedNugget,
        updateContentItemHiddenStatus,
        selectedProjectElementNode,
        projectProductTypeProductListingsInfo,
        dtv,
        isProductTypeElement,
    ])

    React.useEffect(() => {
        setIsWorkspaceEditable(selectedProjectElementNode?.isInProject ?? false)
        setAreSharedPropertiesVisible(
            contentTeamFromStudioPage
                ? true
                : (getFromLocalStorage(
                    kpUserId,
                    projectId!,
                    `${selectedProjectElementNode?.id}-${selectedProjectElementNode?.baseElementId}-isSharedVisible`
                ) as boolean) ??
                selectedProjectElementNode?.elementType ===
                'PRODUCTFAMILY'
        )
    }, [
        selectedProjectElementNode,
        kpUserId,
        projectId,
        contentTeamFromStudioPage,
    ])

    React.useEffect(() => {
        if (
            articleIdToFocus &&
            tabToFocus &&
            tabToFocus?.toLowerCase() === getTabName(tabSelected)?.toLowerCase()
        ) {
            if (
                !expandedArticlesList?.find(
                    (articleId) => articleId === articleIdToFocus
                ) &&
                propertyContentItemId !== articleIdToFocus
            ) {
                updateExpandedArticlesList(articleIdToFocus)
            }
        }
    }, [
        articleIdToFocus,
        tabSelected,
        updateExpandedArticlesList,
        tabToFocus,
        propertyContentItemId,
        expandedArticlesList,
    ])

    React.useEffect(() => {
        if (
            tabToFocus &&
            tabToFocus?.toLowerCase() !== getTabName(tabSelected)?.toLowerCase()
        ) {
            const tabNumber =
                tabToFocus?.toLowerCase() === 'general'
                    ? 0
                    : tabToFocus?.toLowerCase() === 'products'
                        ? 1
                        : 2
            if (isBetaWorkspaceToggleEnabled) {
                setWorkspaceLoadingState(true)
            }
            if (selectedProjectElementNode?.elementType === 'PRODUCTFAMILY') {
                handleFamilyTabSelect(tabNumber)
                startTransition(() =>
                    getSelectedProductFamily({
                        variables: {
                            elementId: selectedProjectElementNode.elementId,
                            projectId,
                            partType: WorkspaceTabTitles[tabNumber ?? 0],
                            familyNodeId: familySelected?.familyNodeId,
                        },
                    })
                )
            } else if (isProductTypeElement) {
                handleProductTypeTabSelect(tabNumber)
                startTransition(() =>
                    getSelectedNugget({
                        variables: {
                            elementId: selectedProjectElementNode?.elementId,
                            projectId,
                            nodeId: selectedProjectElementNode?.id,
                            productFamilyId: projectProductFamilyId,
                            partType: WorkspaceTabTitles[tabNumber ?? 0],
                        },
                    })
                )
            }
        }
    }, [
        tabSelected,
        tabToFocus,
        selectedProjectElementNode,
        handleFamilyTabSelect,
        handleProductTypeTabSelect,
        getSelectedNugget,
        getSelectedProductFamily,
        projectId,
        isProductTypeElement,
        projectProductFamilyId,
        isBetaWorkspaceToggleEnabled,
    ])

    React.useEffect(() => {
        return () => {
            contentItemWorker.terminate()
        }
    }, [])

    React.useEffect(() => {
        if (
            !!selectedProjectElementNode &&
            selectedProjectElementNode?.elementType !==
            ProjectElementType.ASSEMBLY
        ) {
            retrieveUpdatedDtv()
        }
    }, [isTrackChangeToggleOn]) // eslint-disable-line
    //only need trackChangeToggle state as dependency.

    // side-effect to determine if selectedProjectElement on workspace isImportedToProject (current workspace project)
    React.useEffect(() => {
        setReactiveWorkspaceContents({
            isProjectImported:
                selectedProjectElementNode?.isImportedToProject &&
                selectedProjectElementNode?.importedToProjectId === projectId,
        })
    }, [
        selectedProjectElementNode?.isImportedToProject,
        selectedProjectElementNode?.importedToProjectId,
        projectId,
    ])

    React.useEffect(() => {
        updateSelectedNuggetData?.(selectedNuggetData)
    }, [selectedNuggetData, updateSelectedNuggetData])

    React.useEffect(() => {
        if (
            showRightMostPane &&
            rightMostPaneTab === RightMostPaneTabs.AskDela &&
            isBetaWorkspaceToggleEnabled
        ) {
            setRightMostPaneTab(RightMostPaneTabs.TrackChanges)
        }
    }, [
        showRightMostPane,
        rightMostPaneTab,
        isBetaWorkspaceToggleEnabled,
        setRightMostPaneTab,
    ])

    return (
        <div
            className={clsx(classes.container, classes.fullHeight)}
            data-testid="dvWorkspaceContentArea"
        >
            <GridContainer
                direction="column"
                wrap="nowrap"
                className={classes.fullHeight}
            >
                {!isBetaWorkspaceToggleEnabled && (
                    <WorkspaceContentAreaHeader
                        projectId={projectId}
                        allElementsList={allElementsList}
                        selectedTopic={selectedTopic}
                        handleUpdateSelectedTopic={handleUpdateSelectedTopic}
                        updateElementList={updateElementList}
                        famNumberFormat={projectSettings?.familyNumberFormat}
                        isOnlyAddedToProject={isOnlyAddedToProject}
                        setIsAddRemoveClicked={setIsAddRemoveClicked}
                        drawingDesignation={
                            selectedNuggetData?.getProjectNugget
                                .drawingDesignation ?? ''
                        }
                        handleFamilyReorderTemplates={
                            handleFamilyReorderTemplates
                        }
                        projectNuggetId={
                            selectedNuggetData?.getProjectNugget?.id
                        }
                        handleProductTypeReorderTemplates={
                            handleProductTypeReorderTemplates
                        }
                        insertNewProductTypeElement={
                            insertNewProductTypeElement
                        }
                        selectedElementFormat={selectedElementFormat}
                        getPersistedState={getPersistedState}
                        contentTeamFromStudioPage={contentTeamFromStudioPage}
                        isDraftMode={isDraftMode}
                        setIsDraftMode={setIsDraftMode}
                        isInProject={selectedProjectElementNode?.isInProject}
                        fetchSelectedProductFamilyWithTable={
                            fetchSelectedProductFamilyWithTable
                        }
                        handleRetrieveUpdatedDtvPerPartType={
                            retrieveUpdatedDtvPerPartType
                        }
                    />
                )}
                <WorkspaceContentProvider
                    updateFamilyArticleData={updateFamilyArticleData}
                    updateProductArticleData={updateProductArticleData}
                    handleRetrieveUpdatedDTV={retrieveUpdatedDtv}
                    contentItemWorker={contentItemWorker}
                    updateCategoryElementState={updateCategoryElementState}
                    handleRetrieveUpdatedDtvPerPartType={
                        retrieveUpdatedDtvPerPartType
                    }
                    fetchSelectedProductFamilyWithTable={
                        fetchSelectedProductFamilyWithTable
                    }
                    refetchSelectedProductsCount={refetchSelectedProductsCount}
                    setContentIdsToReplaceOnNextFetch={
                        setContentIdsToReplaceOnNextFetch
                    }
                >
                    <GridContainer
                        item
                        direction="row"
                        className={clsx(classes.formAreaContainer, {
                            [classes.workspaceFormAreaContainer]:
                                selectedProjectElementNode?.elementType ===
                                'PRODUCTFAMILY' ||
                                selectedProjectElementNode?.elementType ===
                                'PRODUCTTYPE' ||
                                selectedProjectElementNode?.elementType ===
                                'ASSEMBLY',
                        })}
                    >
                        <GridItem
                            xs={true}
                            className={classes.formArea}
                            data-testid="workspaceFormArea"
                        >
                            {isBetaWorkspaceToggleEnabled ? (
                                <WorkspaceEditor
                                    selectedProjectElementNode={
                                        selectedProjectElementNode
                                    }
                                    selectedElementData={selectedElementData}
                                    projectId={projectId}
                                    isElementTreeLoading={isDataLoading}
                                    insertNewProductTypeElement={
                                        insertNewProductTypeElement
                                    }
                                />
                            ) : (
                                <>
                                    {!selectedTopic &&
                                        selectedProjectElementNode?.elementType ===
                                        'ASSEMBLY' &&
                                        (isDataLoading ? (
                                            <TableLoadingSkeleton
                                                height={85}
                                                pageSize={3}
                                            />
                                        ) : (
                                            <ReviewAssemblyComponent
                                                projectElementNode={
                                                    selectedProjectElementNode
                                                }
                                                selectedElementData={
                                                    selectedElementData
                                                }
                                                setPropertyValue={
                                                    setPropertyValue
                                                }
                                                propertyValue={propertyValue}
                                                handleUpdatePUCPropertyValue={
                                                    handleUpdatePUCPropertyValue
                                                }
                                                contentTeamFromStudioPage={
                                                    contentTeamFromStudioPage
                                                }
                                            />
                                        ))}

                                    {(selectedProjectElementNode?.elementType ===
                                        'PRODUCTFAMILY' ||
                                        selectedTopic) && (
                                            <AreaProvider
                                                projectId={projectId}
                                                type={
                                                    selectedTopic
                                                        ? 'Sustainability Topic'
                                                        : isStudioManagement
                                                            ? 'All'
                                                            : 'Family'
                                                }
                                                productFamily={
                                                    selectedProductFamily
                                                }
                                                setTab={handleFamilyTabSelect}
                                                areNotesVisible={
                                                    isElementNotesVisible ?? true
                                                }
                                                itemsExpanded={familyItemsExpanded}
                                                handleExpandClick={
                                                    handleFamilyExpandClick
                                                }
                                                selectedElementId={
                                                    familySelected?.elementId ?? ''
                                                }
                                                handleFamilyReorderTemplates={
                                                    handleFamilyReorderTemplates
                                                }
                                                areHiddenPropertiesVisible={
                                                    isElementPropertiesVisible ??
                                                    true
                                                }
                                                handleInActivateProperty={
                                                    handleInActivateProperty
                                                }
                                                handleBestPracticeProperty={
                                                    handleBestPracticeProperty
                                                }
                                                selectedProjectNode={
                                                    selectedProjectElementNode
                                                }
                                                unitsOfMeasure={
                                                    contentTeamFromStudioPage
                                                        ? UnitsOfMeasure.BOTH
                                                        : projectSettings?.unitOfMeasure
                                                }
                                                updateExpandState={
                                                    updateExpandedArticlesList
                                                }
                                                expandStateList={
                                                    expandedArticlesList
                                                }
                                                handleAddNewProperty={
                                                    handleAddNewProperty
                                                }
                                                handleAddSustainabilityTopic={
                                                    handleAddSustainabilityTopic
                                                }
                                                handleEditKeyChanges={
                                                    handleEditKeyChanges
                                                }
                                                retrieveUpdatedElementChanges={
                                                    retrieveUpdatedElementChanges
                                                }
                                                selectedSpecAgentIdPt={
                                                    selectedSpecAgentIdPt
                                                }
                                                selectedTopic={selectedTopic}
                                                getPersistedState={
                                                    getPersistedState
                                                }
                                                handleHtmlParagraphPaste={
                                                    handleHtmlParagraphPaste
                                                }
                                                insertNewProductTypeElement={
                                                    insertNewProductTypeElement
                                                }
                                                addNewProductTypeIntoTree={
                                                    addNewProductTypeIntoTree
                                                }
                                            >
                                                <FamilyArea
                                                    handleDeleteProperty={
                                                        handleDeleteProperty
                                                    }
                                                    partType={
                                                        selectedTopic
                                                            ? 'ContentGroup'
                                                            : isStudioManagement
                                                                ? 'All'
                                                                : tabSelected === 0
                                                                    ? 'General'
                                                                    : tabSelected === 1
                                                                        ? 'Products'
                                                                        : 'Execution'
                                                    }
                                                    productFamily={
                                                        selectedProductFamily
                                                    }
                                                    loading={
                                                        productFamilyDtvLoading ||
                                                        projectProductFamilyDtvLoading ||
                                                        selectedProjectProductFamilyDtvLoading
                                                    }
                                                    familyLoading={familyLoading}
                                                    handleContentItemUpdate={
                                                        handleContentItemUpdate
                                                    }
                                                    contentTeamFromStudioPage={
                                                        contentTeamFromStudioPage
                                                    }
                                                    isDraftMode={isDraftMode}
                                                    selectedTopic={selectedTopic}
                                                    dtv={dtv}
                                                    productTypeProperties={
                                                        productTypeProperties
                                                    }
                                                    contentItemNodes={
                                                        contentItemNodes
                                                    }
                                                    tableList={tableList}
                                                    tabSelected={tabSelected}
                                                    projectProductFamilyId={
                                                        projectProductFamilyId
                                                    }
                                                    isAddProhibited={
                                                        isAddProhibited
                                                    }
                                                    addQueueSib={addQueueSib}
                                                    elementIdOnEdit={
                                                        elementIdOnEdit
                                                    }
                                                    isAddOngoing={isAddOngoing}
                                                    isNewAddOnQueue={
                                                        isNewAddOnQueue
                                                    }
                                                    familyAddedProductTypes={
                                                        familyAddedProductTypes
                                                    }
                                                    getUpdatedNugget={
                                                        retrieveUpdatedNugget
                                                    }
                                                />
                                            </AreaProvider>
                                        )}
                                    {selectedProjectElementNode?.elementType ===
                                        'PRODUCTTYPE' && (
                                            <AreaProvider
                                                type={
                                                    isStudioManagement
                                                        ? 'All'
                                                        : 'ProductType'
                                                }
                                                projectId={projectId}
                                                nugget={
                                                    selectedProjectElementNode?.elementId
                                                        ? (projectProductTypeProductListingsInfo as Partial<
                                                            GetProjectNuggetResponse
                                                        >)?.getProjectNugget
                                                        : (projectProductTypeProductListingsInfo as Partial<
                                                            GetNuggetResponse
                                                        >)?.nugget
                                                }
                                                manufacturerArticleIds={
                                                    manufacturerArticleIds
                                                }
                                                selectedTopic={selectedTopic}
                                                setTab={handleProductTypeTabSelect}
                                                areNotesVisible={
                                                    isElementNotesVisible ?? true
                                                }
                                                itemsExpanded={
                                                    productTypeItemsExpanded
                                                }
                                                handleExpandClick={
                                                    handleProductTypeExpandClick
                                                }
                                                selectedElementId={
                                                    selectedProjectElementNode?.id ??
                                                    ''
                                                }
                                                handleProductTypeReorderTemplates={
                                                    handleProductTypeReorderTemplates
                                                }
                                                areHiddenPropertiesVisible={
                                                    isElementPropertiesVisible ??
                                                    true
                                                }
                                                handleInActivateProperty={
                                                    handleInActivateProperty
                                                }
                                                handleBestPracticeProperty={
                                                    handleBestPracticeProperty
                                                }
                                                selectedProjectNode={
                                                    selectedProjectElementNode
                                                }
                                                productFamily={
                                                    isStudioManagement &&
                                                        projectProductFamilyData?.getProjectProductFamily
                                                        ? projectProductFamilyData?.getProjectProductFamily
                                                        : productFamilyData?.productFamily ??
                                                        {}
                                                }
                                                persistState={persistState}
                                                getPersistedState={
                                                    getPersistedState
                                                }
                                                unitsOfMeasure={
                                                    contentTeamFromStudioPage
                                                        ? UnitsOfMeasure.BOTH
                                                        : projectSettings?.unitOfMeasure
                                                }
                                                updateExpandState={
                                                    updateExpandedArticlesList
                                                }
                                                expandStateList={
                                                    expandedArticlesList
                                                }
                                                setManufacturerArticleId={
                                                    setManufacturerArticleId
                                                }
                                                handleAddNewProperty={
                                                    handleAddNewProperty
                                                }
                                                handleAddSustainabilityTopic={
                                                    handleAddSustainabilityTopic
                                                }
                                                handleEditKeyChanges={
                                                    handleEditKeyChanges
                                                }
                                                retrieveUpdatedElementChanges={
                                                    retrieveUpdatedElementChanges
                                                }
                                                selectedSpecAgentIdPt={
                                                    selectedSpecAgentIdPt
                                                }
                                                handleHtmlParagraphPaste={
                                                    handleHtmlParagraphPaste
                                                }
                                                addNewProductTypeIntoTree={
                                                    addNewProductTypeIntoTree
                                                }
                                            >
                                                <PropertiesArea
                                                    nugget={getNuggetForProductType(
                                                        nuggetData?.nugget,
                                                        selectedNuggetData?.getProjectNugget,
                                                        selectedProjectElementNode?.elementId ??
                                                        selectedProjectElementNode?.baseElementId
                                                    )}
                                                    loading={
                                                        !contentTeamFromStudioPage &&
                                                        (dtv === null ||
                                                            (nuggetData ===
                                                                undefined &&
                                                                selectedNuggetData ===
                                                                undefined))
                                                    }
                                                    handleContentItemUpdate={
                                                        handleContentItemUpdate
                                                    }
                                                    handleDeleteProperty={
                                                        handleDeleteProperty
                                                    }
                                                    getUpdatedNugget={
                                                        retrieveUpdatedNugget
                                                    }
                                                    isDraftMode={isDraftMode}
                                                    contentTeamFromStudioPage={
                                                        contentTeamFromStudioPage
                                                    }
                                                />
                                            </AreaProvider>
                                        )}
                                </>
                            )}
                            <Backdrop
                                className={classes.backdrop}
                                open={
                                    !isBetaWorkspaceToggleEnabled &&
                                    (loadingBackdrop ||
                                        (productFamilyData === null &&
                                            projectProductFamilyData ===
                                            null) ||
                                        (nuggetData === null &&
                                            selectedNuggetData === null) ||
                                        addNewPT)
                                }
                            >
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        </GridItem>
                        {isBetaWorkspaceToggleEnabled ? (
                            <GridItem
                                className={clsx(
                                    selectedProjectElementNode ||
                                        (selectedTopic &&
                                            contentTeamFromStudioPage)
                                        ? classes.propertyExpandedPane
                                        : classes.propertyCollapsedPane,
                                    !showRightMostPane &&
                                    classes.collapsedRightPane
                                )}
                            >
                                {(selectedProjectElementNode ||
                                    (selectedTopic &&
                                        contentTeamFromStudioPage)) && (
                                        <RightMostPaneBeta
                                            key={
                                                selectedProjectElementNode?.baseElementId
                                            }
                                            drawingDesignation={
                                                selectedNuggetData?.getProjectNugget
                                                    ?.drawingDesignation ?? ''
                                            }
                                            toggleHandler={elementToggleHandler}
                                            contentTeamFromStudioPage={
                                                contentTeamFromStudioPage
                                            }
                                            showRightMostPane={showRightMostPane}
                                            setLoadingBackdrop={setLoadingBackdrop}
                                            selectedTopic={selectedTopic}
                                            setRightMostPaneTab={
                                                setRightMostPaneTab
                                            }
                                            selectedProjectElementNode={
                                                selectedProjectElementNode
                                            }
                                        />
                                    )}
                            </GridItem>
                        ) : (
                            <GridItem
                                className={clsx(
                                    !isBetaWorkspaceToggleEnabled &&
                                    selectedProjectElementNode?.isInProject &&
                                    classes.propertiesPane,
                                    (selectedProjectElementNode &&
                                        selectedProjectElementNode?.elementId) ||
                                        (selectedTopic &&
                                            contentTeamFromStudioPage)
                                        ? classes.propertyExpandedPane
                                        : classes.propertyCollapsedPane,
                                    !showRightMostPane &&
                                    classes.collapsedRightPane,
                                    {
                                        [classes.askDelaWidth]:
                                            showRightMostPane &&
                                            rightMostPaneTab ===
                                            RightMostPaneTabs.AskDela,
                                    }
                                )}
                            >
                                <HoverTooltip
                                    text={
                                        showRightMostPane
                                            ? 'Hide side panel'
                                            : 'Show side panel'
                                    }
                                    placements={['left']}
                                >
                                    <div
                                        className={clsx(
                                            classes.expandBtn,
                                            !showRightMostPane &&
                                            classes.collapseBtn
                                        )}
                                        onClick={() => {
                                            setShowRightMostPane(
                                                !showRightMostPane
                                            )
                                            if (propertyToFocus) {
                                                resetOnFocusProperty()
                                            }
                                            if (articleIdToFocus) {
                                                setArticleToFocus(undefined)
                                            }
                                        }}
                                    >
                                        <DoubleArrowRoundedIcon fontSize="default" />
                                    </div>
                                </HoverTooltip>
                                {((selectedProjectElementNode &&
                                    selectedProjectElementNode?.elementId) ||
                                    (selectedTopic &&
                                        contentTeamFromStudioPage)) && (
                                        <RighMostPane
                                            key={
                                                selectedProjectElementNode?.baseElementId
                                            }
                                            drawingDesignation={
                                                selectedNuggetData?.getProjectNugget
                                                    ?.drawingDesignation ?? ''
                                            }
                                            isProjectPropertiesHidden={true}
                                            isTrackChangesPaneHidden={false}
                                            toggleHandler={elementToggleHandler}
                                            contentTeamFromStudioPage={
                                                contentTeamFromStudioPage
                                            }
                                            showRightMostPane={showRightMostPane}
                                            setLoadingBackdrop={setLoadingBackdrop}
                                            selectedTopic={selectedTopic}
                                            setShowRightMostPane={
                                                setShowRightMostPane
                                            }
                                            setRightMostPaneTab={
                                                setRightMostPaneTab
                                            }
                                            selectedProjectElementNode={
                                                selectedProjectElementNode
                                            }
                                        />
                                    )}
                            </GridItem>
                        )}
                        {isPasteInProgress && (
                            <HtmlParagraphPasteProgressDialog
                                selectedProjectElementNode={
                                    selectedProjectElementNode
                                }
                                contentInsertVariables={contentInsertVariables}
                                onPasteError={handleHtmlParagraphPasteError}
                                onPasteCompleted={
                                    handleHtmlParagraphPasteCompleted
                                }
                                onPasteCancelled={
                                    handleHtmlParagraphPasteCancelled
                                }
                            />
                        )}
                    </GridContainer>
                </WorkspaceContentProvider>
            </GridContainer>
        </div>
    )
}

export default WorkspaceContentArea
